// src/http/delivery/index.ts
import { initContract as initContract9 } from "@ts-rest/core";
import { z as z10 } from "zod";
import {
  DeliveryProofAssetTypeSchema,
  ShipmentLegMetrcReconciliationIssueSchema,
  ShipmentLegMetrcReconciliationMetricsSchema
} from "@flote/types/delivery";

// src/http/delivery/schemas.ts
import { z } from "zod";
var DeliveryTaskStatusSchema = z.enum([
  "pending",
  "assigned",
  "in_transit",
  "arrived",
  "completed",
  "failed"
]);
var DeliveryTaskTypeSchema = z.enum(["pickup", "dropoff"]);
var DriverStatusSchema = z.enum(["off_duty", "idle", "active"]);
var VehicleTypeSchema = z.enum(["CAR", "MOTORCYCLE", "BICYCLE", "TRUCK"]);
var DeliveryTaskSchema = z.object({
  id: z.string(),
  onfleetTaskId: z.string(),
  orderId: z.number().optional(),
  trackingId: z.string(),
  status: DeliveryTaskStatusSchema,
  taskType: DeliveryTaskTypeSchema,
  linkedTaskId: z.string().optional(),
  onfleetDependencies: z.array(z.string()).optional(),
  recipientName: z.string(),
  recipientPhone: z.string().optional(),
  address: z.object({
    number: z.string(),
    street: z.string(),
    apartment: z.string().optional(),
    city: z.string(),
    state: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().default("USA")
  }),
  location: z.object({
    lat: z.number(),
    lng: z.number()
  }).optional(),
  notes: z.string().optional(),
  estimatedArrival: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  failureReason: z.string().optional(),
  failureNotes: z.string().optional(),
  proofOfDelivery: z.object({
    signatureUploadId: z.string().optional(),
    photoUploadIds: z.array(z.string()).optional(),
    signatureUrl: z.string().optional(),
    photoUrls: z.array(z.string()).optional(),
    notes: z.string().optional()
  }).optional(),
  driver: z.object({
    id: z.string(),
    name: z.string(),
    phone: z.string().optional(),
    photoUrl: z.string().optional(),
    vehicleDescription: z.string().optional(),
    location: z.object({
      lat: z.number(),
      lng: z.number()
    }).optional()
  }).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var DriverSchema = z.object({
  id: z.string(),
  onfleetWorkerId: z.string(),
  name: z.string(),
  displayName: z.string().optional(),
  phone: z.string(),
  metrcEmployeeId: z.string().optional(),
  driversLicenseNumber: z.string().optional(),
  status: DriverStatusSchema,
  onDuty: z.boolean(),
  location: z.object({
    lat: z.number(),
    lng: z.number()
  }).optional(),
  teamId: z.string().optional(),
  teamIds: z.array(z.string()).optional(),
  vehicleId: z.string().nullable().optional(),
  vehicle: z.object({
    type: z.string(),
    description: z.string().optional(),
    licensePlate: z.string().optional(),
    color: z.string().optional()
  }).optional(),
  photoUrl: z.string().optional(),
  activeTaskCount: z.number().optional(),
  lastSeenAt: z.string().datetime().optional(),
  onfleetUpdatedAt: z.string().datetime().optional(),
  createdBySupplierId: z.string().uuid().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
var TeamSchema = z.object({
  id: z.string(),
  onfleetTeamId: z.string(),
  name: z.string(),
  hub: z.string().nullable(),
  enableSelfAssignment: z.boolean(),
  taskCount: z.number(),
  workerCount: z.number().optional(),
  managerCount: z.number().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

// src/http/delivery/assignment-queue.contract.ts
import { initContract } from "@ts-rest/core";
import { z as z2 } from "zod";
import {
  AssignmentQueueStatsSchema,
  AssignmentQueueStatusSchema,
  AssignmentQueueEntryDtoSchema
} from "@flote/types/delivery";
var c = initContract();
var errorResponse = z2.object({ message: z2.string() });
var ListAssignmentQueueQuerySchema = z2.object({
  status: AssignmentQueueStatusSchema.optional(),
  stockLocationId: z2.string().uuid().optional(),
  limit: z2.coerce.number().default(50)
});
var GetAssignmentQueueStatsQuerySchema = z2.object({
  stockLocationId: z2.string().uuid().optional()
});
var EnrichedQueueEntrySchema = AssignmentQueueEntryDtoSchema.extend({
  trackingId: z2.string(),
  customerName: z2.string().optional(),
  portionTotal: z2.number()
});
var QueueListResponseSchema = z2.object({
  entries: z2.array(EnrichedQueueEntrySchema),
  stats: AssignmentQueueStatsSchema
});
var UpdatePriorityRequestSchema = z2.object({
  priority: z2.number().int().min(0).max(100)
});
var assignmentQueueContract = c.router({
  // List queue entries for the authenticated supplier
  listQueue: {
    method: "GET",
    path: "/api/supplier/assignment-queue",
    query: ListAssignmentQueueQuerySchema,
    responses: {
      200: QueueListResponseSchema,
      401: errorResponse,
      403: errorResponse,
      500: errorResponse
    },
    summary: "List assignment queue entries for supplier"
  },
  // Get queue statistics only
  getStats: {
    method: "GET",
    path: "/api/supplier/assignment-queue/stats",
    query: GetAssignmentQueueStatsQuerySchema,
    responses: {
      200: AssignmentQueueStatsSchema,
      401: errorResponse,
      403: errorResponse,
      500: errorResponse
    },
    summary: "Get assignment queue statistics"
  },
  // Update priority for a queue entry
  updatePriority: {
    method: "PATCH",
    path: "/api/supplier/assignment-queue/:entryId/priority",
    pathParams: z2.object({ entryId: z2.string().uuid() }),
    body: UpdatePriorityRequestSchema,
    responses: {
      200: AssignmentQueueEntryDtoSchema,
      400: errorResponse,
      401: errorResponse,
      403: errorResponse,
      404: errorResponse,
      500: errorResponse
    },
    summary: "Update queue entry priority"
  }
});
var EnqueuePartitionRequestSchema = z2.object({
  partitionId: z2.string().uuid(),
  /** Organization that owns this queue entry (FK to organization.organizations) */
  organizationId: z2.string().uuid().optional(),
  /** Supplier who owns this queue entry. DEPRECATED — use organizationId. */
  supplierId: z2.string().uuid().optional(),
  /** Stock location ID for location-scoped queue */
  stockLocationId: z2.string().uuid().optional(),
  /** Order value for priority calculation */
  orderValue: z2.number().optional(),
  /** Assignment window expiration */
  expiresAt: z2.string().datetime().optional()
});
var EnqueuePartitionResponseSchema = z2.object({
  entry: AssignmentQueueEntryDtoSchema,
  alreadyExists: z2.boolean()
});
var AutoEnqueueRequestSchema = z2.object({
  partitionId: z2.string().uuid()
});
var AutoEnqueueResponseSchema = z2.object({
  enqueued: z2.boolean(),
  reason: z2.string(),
  entryId: z2.string().uuid().optional()
});
var internalAssignmentQueueContract = c.router({
  enqueue: {
    method: "POST",
    path: "/api/internal/assignment-queue/enqueue",
    body: EnqueuePartitionRequestSchema,
    responses: {
      201: EnqueuePartitionResponseSchema,
      400: errorResponse,
      401: errorResponse,
      404: errorResponse,
      500: errorResponse
    },
    summary: "Enqueue a partition for driver assignment (event-worker)"
  },
  autoEnqueue: {
    method: "POST",
    path: "/api/internal/assignment-queue/auto-enqueue",
    body: AutoEnqueueRequestSchema,
    responses: {
      200: AutoEnqueueResponseSchema,
      404: errorResponse,
      500: errorResponse
    },
    summary: "Auto-enqueue a partition \u2014 resolves location/supplier from DB, skips if pending offer exists"
  }
});

// src/http/delivery/shipment-legs.contract.ts
import { initContract as initContract2 } from "@ts-rest/core";
import { z as z3 } from "zod";
import {
  DeliveryDownstreamTaskDraftDtoSchema,
  DeliveryShipmentDtoSchema,
  DeliveryShipmentLegAllocationDtoSchema,
  DeliveryShipmentLegTypeSchema,
  DeliveryShipmentLegDtoSchema,
  DeliveryShipmentLegQueueListItemDtoSchema,
  DeliveryShipmentLegQueueSummaryDtoSchema,
  DeliveryShipmentLegQueueStatsSchema,
  DeliveryShipmentLegStatusSchema,
  DeliveryShipmentStatusSchema,
  DeliveryShipmentTimelineItemDtoSchema,
  DeliveryShipmentLegManifestDtoSchema,
  DeliveryShipmentLegManifestReadinessDtoSchema,
  DeliveryShipmentPlanNodeDtoSchema,
  DeliveryShipmentPartitionAllocationDtoSchema,
  DeliveryShipmentManifestDtoSchema,
  DispatchShipmentResultSchema
} from "@flote/types/delivery";
var c2 = initContract2();
var errorResponse2 = z3.object({ message: z3.string() });
var CreateShipmentFromPartitionsConflictSchema = z3.object({
  message: z3.string(),
  code: z3.literal("SHIPMENT_PARTITIONS_ALREADY_ALLOCATED").optional(),
  conflictPartitionIds: z3.array(z3.string().uuid()).optional(),
  conflictDisplayReferences: z3.array(z3.string()).optional(),
  existingShipmentIds: z3.array(z3.string().uuid()).optional()
});
var ListShipmentLegQueueQuerySchema = z3.object({
  status: z3.enum(["pending", "processing", "assigned", "expired", "cancelled"]).optional(),
  stockLocationId: z3.string().uuid().optional(),
  limit: z3.coerce.number().int().positive().default(25),
  offset: z3.coerce.number().int().nonnegative().default(0)
});
var ShipmentLegQueueListResponseSchema = z3.object({
  entries: z3.array(DeliveryShipmentLegQueueListItemDtoSchema),
  stats: DeliveryShipmentLegQueueStatsSchema,
  total: z3.number().int().nonnegative(),
  nextOffset: z3.number().int().nonnegative().nullable()
});
var ListShipmentSummariesQuerySchema = z3.object({
  status: z3.enum(["pending", "processing", "assigned", "expired", "cancelled"]).optional(),
  shipmentStatuses: z3.preprocess(
    (val) => typeof val === "string" ? val.split(",") : val,
    z3.array(DeliveryShipmentStatusSchema).optional()
  ),
  stockLocationId: z3.string().uuid().optional(),
  limit: z3.coerce.number().int().positive().default(25),
  offset: z3.coerce.number().int().nonnegative().default(0)
});
var ShipmentQueueSummariesResponseSchema = z3.object({
  shipments: z3.array(DeliveryShipmentLegQueueSummaryDtoSchema),
  total: z3.number().int().nonnegative(),
  nextOffset: z3.number().int().nonnegative().nullable()
});
var AssignDriverToShipmentLegRequestSchema = z3.object({
  driverId: z3.string().uuid()
});
var AssignDriverToShipmentLegResponseSchema = z3.object({
  legId: z3.string().uuid(),
  driverId: z3.string().uuid(),
  status: DeliveryShipmentLegStatusSchema
});
var CreateShipmentPlanRequestSchema = z3.object({
  sourceStockLocationId: z3.string().uuid(),
  destinationLocationId: z3.string().uuid().nullable().optional(),
  partitionIds: z3.array(z3.string().uuid()).min(1),
  legType: DeliveryShipmentLegTypeSchema.default("linehaul"),
  queueForAssignment: z3.boolean().default(true),
  priority: z3.number().int().optional(),
  notes: z3.string().optional()
}).refine(
  (data) => data.legType === "final_mile" || !!data.destinationLocationId,
  { message: "destinationLocationId is required for non-final-mile leg types", path: ["destinationLocationId"] }
);
var CreateShipmentPlanQueueErrorSchema = z3.object({
  legId: z3.string().uuid(),
  message: z3.string()
});
var CreateShipmentPlanResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  legIds: z3.array(z3.string().uuid()),
  queuedLegIds: z3.array(z3.string().uuid()),
  allocationCount: z3.number().int().nonnegative(),
  queueErrors: z3.array(CreateShipmentPlanQueueErrorSchema)
});
var DispatchShipmentRequestSchema = z3.object({
  plannedDepartureTime: z3.number().int().positive().optional()
});
var AddHubSplitRequestSchema = z3.object({
  fromLegId: z3.string().uuid(),
  hubName: z3.string().min(1).optional(),
  hubStockLocationId: z3.string().uuid().optional(),
  terminalPartitionIds: z3.array(z3.string().uuid()).default([]),
  driverId: z3.string().uuid().optional(),
  queueForAssignment: z3.boolean().default(false),
  notes: z3.string().optional()
});
var AddHubSplitResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  hubNodeId: z3.string().uuid(),
  fromLegId: z3.string().uuid(),
  terminalCount: z3.number().int().nonnegative(),
  continuingCount: z3.number().int().nonnegative(),
  createdLegIds: z3.array(z3.string().uuid()),
  assignedDriverId: z3.string().uuid().nullable().optional(),
  queuedLegIds: z3.array(z3.string().uuid()).default([])
});
var BatchInsertHubRequestSchema = z3.object({
  legIds: z3.array(z3.string().uuid()).min(1),
  hubName: z3.string().min(1),
  hubStockLocationId: z3.string().uuid().optional(),
  inboundDriverId: z3.string().uuid().optional(),
  layoverSchedule: z3.object({
    checkInAt: z3.string(),
    checkOutAt: z3.string()
  }).optional()
});
var BatchInsertHubResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  hubNodeId: z3.string().uuid(),
  inboundLegId: z3.string().uuid(),
  outboundLegIds: z3.array(z3.string().uuid()),
  removedLegIds: z3.array(z3.string().uuid())
});
var UpdateHubMetadataRequestSchema = z3.object({
  hubNodeId: z3.string().uuid(),
  layoverSchedule: z3.object({
    checkInAt: z3.string(),
    checkOutAt: z3.string()
  }).nullable()
});
var UpdateHubMetadataResponseSchema = z3.object({
  hubNodeId: z3.string().uuid(),
  metadata: z3.record(z3.unknown())
});
var RemoveHubSplitRequestSchema = z3.object({
  hubNodeId: z3.string().uuid()
});
var RemoveHubSplitResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  removedHubNodeId: z3.string().uuid(),
  restoredLegId: z3.string().uuid(),
  deletedLegCount: z3.number().int().nonnegative(),
  restoredPartitionCount: z3.number().int().nonnegative()
});
var InsertWaypointRequestSchema = z3.object({
  fromLegId: z3.string().uuid(),
  hubName: z3.string().min(1).optional(),
  hubStockLocationId: z3.string().uuid().optional(),
  driverId: z3.string().uuid().optional(),
  queueForAssignment: z3.boolean().default(false),
  notes: z3.string().optional()
});
var InsertWaypointResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  hubNodeId: z3.string().uuid(),
  fromLegId: z3.string().uuid(),
  createdLegId: z3.string().uuid(),
  assignedDriverId: z3.string().uuid().nullable(),
  queuedLegId: z3.string().uuid().nullable()
});
var ReorderStopsRequestSchema = z3.object({
  orderedDestinationNodeIds: z3.array(z3.string().uuid()).min(1)
});
var ReorderStopsResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  reorderedLegCount: z3.number().int().nonnegative(),
  orderedDestinationNodeIds: z3.array(z3.string().uuid())
});
var OptimizeShipmentRouteRequestSchema = z3.object({});
var OptimizeShipmentRouteResponseSegmentSchema = z3.object({
  driverId: z3.string().uuid().nullable(),
  rewiredLegCount: z3.number().int().nonnegative(),
  orderedNodeIds: z3.array(z3.string().uuid()),
  distanceMeters: z3.number().int().nonnegative().nullable(),
  durationSeconds: z3.number().int().nonnegative().nullable(),
  skipped: z3.enum(["provider_unavailable", "insufficient_stops", "missing_coordinates", "no_optimization"]).nullable()
});
var OptimizeShipmentRouteResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  /** Aggregate (sum) — legs whose (from_node_id, to_node_id) changed. Zero on skip. */
  rewiredLegCount: z3.number().int().nonnegative(),
  /** Aggregate (concat) — destination node IDs in segment-ascending visit order. Empty on skip. */
  orderedNodeIds: z3.array(z3.string().uuid()),
  /** Aggregate (sum) — distance across non-skipped segments. Null when all skipped. */
  distanceMeters: z3.number().int().nonnegative().nullable(),
  /** Aggregate (sum) — duration across non-skipped segments. Null when all skipped. */
  durationSeconds: z3.number().int().nonnegative().nullable(),
  /** Aggregate — null if any segment optimized; otherwise the first non-null skip reason. */
  skipped: z3.enum(["provider_unavailable", "insufficient_stops", "missing_coordinates", "no_optimization"]).nullable(),
  /** Per-driver-segment breakdown. Empty only on hard early returns. */
  segments: z3.array(OptimizeShipmentRouteResponseSegmentSchema)
});
var CreateShipmentFromPartitionsRequestSchema = z3.object({
  partitionIds: z3.array(z3.string().uuid()).min(1),
  queueForAssignment: z3.boolean().default(true),
  notes: z3.string().optional()
});
var CreateShipmentFromPartitionsResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  legCount: z3.number().int().positive(),
  destinationCount: z3.number().int().nonnegative(),
  untrackedPartitionCount: z3.number().int().nonnegative(),
  queuedLegIds: z3.array(z3.string().uuid())
});
var AssignDestinationLicenseRequestSchema = z3.object({
  partitionIds: z3.array(z3.string().uuid()).min(1),
  complianceLicenseId: z3.string().uuid()
});
var AssignDestinationLicenseResponseSchema = z3.object({
  destinationNodeId: z3.string().uuid(),
  destinationNodeName: z3.string(),
  complianceLicenseId: z3.string().uuid(),
  assignedPartitionCount: z3.number().int().nonnegative(),
  createdAllocations: z3.number().int().nonnegative()
});
var DriverPositionSchema = z3.object({
  driverId: z3.string().uuid(),
  driverName: z3.string().nullable(),
  lat: z3.number(),
  lng: z3.number(),
  capturedAt: z3.string()
});
var GetShipmentDriverPositionsResponseSchema = z3.object({
  positions: z3.array(DriverPositionSchema)
});
var ShipmentDetailResponseSchema = z3.object({
  shipment: DeliveryShipmentDtoSchema,
  legs: z3.array(DeliveryShipmentLegDtoSchema),
  allocations: z3.array(DeliveryShipmentLegAllocationDtoSchema),
  timeline: z3.array(DeliveryShipmentTimelineItemDtoSchema),
  /** @deprecated Draft concept removed - always returns empty array */
  downstreamDrafts: z3.array(DeliveryDownstreamTaskDraftDtoSchema).default([]),
  /** DAG plan nodes (origin, hub, destination stops) */
  planNodes: z3.array(DeliveryShipmentPlanNodeDtoSchema).default([]),
  /** DAG partition allocations (partitions → destination nodes) */
  dagAllocations: z3.array(DeliveryShipmentPartitionAllocationDtoSchema).default([]),
  /** Per-destination Metrc manifests */
  manifests: z3.array(DeliveryShipmentManifestDtoSchema).default([])
});
var DeleteShipmentRequestSchema = z3.object({
  force: z3.boolean().default(false)
});
var DeleteShipmentResponseSchema = z3.object({
  shipmentId: z3.string().uuid(),
  deletedLegCount: z3.number().int().nonnegative(),
  deletedAllocationCount: z3.number().int().nonnegative(),
  deletedQueueEntryCount: z3.number().int().nonnegative(),
  cancelledOnfleetTaskIds: z3.array(z3.string()),
  warnings: z3.array(z3.string())
});
var DeleteShipmentLegRequestSchema = z3.object({
  force: z3.boolean().default(false)
});
var DeleteShipmentLegResponseSchema = z3.object({
  shipmentLegId: z3.string().uuid(),
  deletedAllocationCount: z3.number().int().nonnegative(),
  deletedQueueEntry: z3.boolean(),
  cancelledOnfleetTaskId: z3.string().nullable(),
  warnings: z3.array(z3.string())
});
var CancelQueueEntryResponseSchema = z3.object({
  shipmentLegId: z3.string().uuid(),
  queueEntryId: z3.string().uuid().nullable(),
  legStatusReverted: z3.boolean()
});
var UpdateShipmentLegRequestSchema = z3.object({
  /** New destination location (only for linehaul legs) */
  destinationLocationId: z3.string().uuid().nullable().optional(),
  /** Driver to assign (or null to clear) */
  assignedDriverId: z3.string().uuid().nullable().optional(),
  /** Notes to update */
  notes: z3.string().optional()
});
var UpdateShipmentLegResponseSchema = z3.object({
  leg: DeliveryShipmentLegDtoSchema
});
var GetShipmentLegManifestResponseSchema = z3.object({
  manifest: DeliveryShipmentLegManifestDtoSchema.nullable()
});
var PrepareShipmentLegManifestRequestSchema = z3.object({
  manifestName: z3.string().min(1).optional(),
  plannedRoute: z3.string().min(1).optional(),
  transferTypeName: z3.string().min(1).optional(),
  force: z3.boolean().default(false),
  metadata: z3.record(z3.unknown()).optional()
});
var PrepareShipmentLegManifestResponseSchema = z3.object({
  manifest: DeliveryShipmentLegManifestDtoSchema,
  readiness: DeliveryShipmentLegManifestReadinessDtoSchema
});
var SubmitShipmentLegManifestRequestSchema = z3.object({
  expectedVersion: z3.number().int().positive().optional(),
  metadata: z3.record(z3.unknown()).optional()
});
var UpdateShipmentLegManifestRequestSchema = z3.object({
  expectedVersion: z3.number().int().positive().optional(),
  metadata: z3.record(z3.unknown()).optional()
});
var VoidShipmentLegManifestRequestSchema = z3.object({
  expectedVersion: z3.number().int().positive().optional(),
  reason: z3.string().min(1).optional(),
  metadata: z3.record(z3.unknown()).optional()
});
var ShipmentLegManifestActionResponseSchema = z3.object({
  manifest: DeliveryShipmentLegManifestDtoSchema
});
var PerDestinationOverridesSchema = z3.object({
  manifestName: z3.string().min(1).optional(),
  estimatedDepartureAt: z3.string().min(1).optional(),
  estimatedArrivalAt: z3.string().min(1).optional()
});
var PrepareShipmentManifestsRequestSchema = z3.object({
  transferTypeName: z3.string().min(1).optional(),
  plannedRoutePrefix: z3.string().min(1).optional(),
  /** Global fallback departure — used when no per-destination override exists */
  estimatedDepartureAt: z3.string().min(1).optional(),
  /** Global fallback arrival — used when no per-destination override exists */
  estimatedArrivalAt: z3.string().min(1).optional(),
  /** Per-destination overrides keyed by destination node ID */
  destinationOverrides: z3.record(z3.string().uuid(), PerDestinationOverridesSchema).optional()
});
var PrepareShipmentManifestsResponseSchema = z3.object({
  manifests: z3.array(DeliveryShipmentManifestDtoSchema),
  skippedDestinations: z3.array(z3.string().uuid())
});
var SubmitShipmentManifestRequestSchema = z3.object({
  expectedVersion: z3.number().int().positive().optional()
});
var ShipmentManifestActionResponseSchema = z3.object({
  manifest: DeliveryShipmentManifestDtoSchema
});
var shipmentLegsContract = c2.router({
  createShipmentPlan: {
    method: "POST",
    path: "/api/supplier/shipments/plan",
    body: CreateShipmentPlanRequestSchema,
    responses: {
      200: CreateShipmentPlanResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      500: errorResponse2
    },
    summary: "Create shipment plan from selected partitions"
  },
  createFromPartitions: {
    method: "POST",
    path: "/api/supplier/shipments/create-from-partitions",
    body: CreateShipmentFromPartitionsRequestSchema,
    responses: {
      200: CreateShipmentFromPartitionsResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      409: CreateShipmentFromPartitionsConflictSchema,
      500: errorResponse2
    },
    summary: "Create shipment from partition IDs (infers topology from partition data)"
  },
  assignDestinationLicense: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/assign-destination-license",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: AssignDestinationLicenseRequestSchema,
    responses: {
      200: AssignDestinationLicenseResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Assign a destination compliance license to partitions on a shipment"
  },
  listQueue: {
    method: "GET",
    path: "/api/supplier/shipment-legs/queue",
    query: ListShipmentLegQueueQuerySchema,
    responses: {
      200: ShipmentLegQueueListResponseSchema,
      401: errorResponse2,
      403: errorResponse2,
      500: errorResponse2
    },
    summary: "List shipment-leg assignment queue"
  },
  listShipmentSummaries: {
    method: "GET",
    path: "/api/supplier/shipments/summaries",
    query: ListShipmentSummariesQuerySchema,
    responses: {
      200: ShipmentQueueSummariesResponseSchema,
      401: errorResponse2,
      403: errorResponse2,
      500: errorResponse2
    },
    summary: "List shipment summaries derived from shipment-leg assignment queue"
  },
  assignDriver: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/assign-driver",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: AssignDriverToShipmentLegRequestSchema,
    responses: {
      200: AssignDriverToShipmentLegResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Assign a driver to a shipment leg"
  },
  addHubSplit: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/hub-split",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: AddHubSplitRequestSchema,
    responses: {
      200: AddHubSplitResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Add a hub split to a shipment DAG (creates hub node + downstream legs)"
  },
  batchInsertHub: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/batch-insert-hub",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: BatchInsertHubRequestSchema,
    responses: {
      200: BatchInsertHubResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Insert a hub across multiple legs (star \u2192 hub-and-spoke)"
  },
  updateHubMetadata: {
    method: "PATCH",
    path: "/api/supplier/shipments/:shipmentId/hub-metadata",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: UpdateHubMetadataRequestSchema,
    responses: {
      200: UpdateHubMetadataResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Update hub node metadata (layover schedule)"
  },
  removeHubSplit: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/remove-hub",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: RemoveHubSplitRequestSchema,
    responses: {
      200: RemoveHubSplitResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Remove a hub node from a shipment DAG (reverses addHubSplit)"
  },
  insertWaypoint: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/insert-waypoint",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: InsertWaypointRequestSchema,
    responses: {
      200: InsertWaypointResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Insert a waypoint into the chain topology (no fan-out)"
  },
  reorderStops: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/reorder-stops",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: ReorderStopsRequestSchema,
    responses: {
      200: ReorderStopsResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Reorder destination stops in a chain topology"
  },
  optimizeRoute: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/optimize-route",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: OptimizeShipmentRouteRequestSchema,
    responses: {
      200: OptimizeShipmentRouteResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      422: errorResponse2,
      500: errorResponse2,
      502: errorResponse2
    },
    summary: "Re-optimize the leg order of a shipment to minimize total drive time"
  },
  getShipmentDetail: {
    method: "GET",
    path: "/api/supplier/shipments/:shipmentId",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    responses: {
      200: ShipmentDetailResponseSchema,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Get shipment detail with timeline and allocations"
  },
  getShipmentDriverPositions: {
    method: "GET",
    path: "/api/supplier/shipments/:shipmentId/driver-positions",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    responses: {
      200: GetShipmentDriverPositionsResponseSchema,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Get latest driver positions for a shipment"
  },
  deleteShipment: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/delete",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: DeleteShipmentRequestSchema,
    responses: {
      200: DeleteShipmentResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Delete a shipment and all associated resources"
  },
  deleteShipmentLeg: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/delete",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: DeleteShipmentLegRequestSchema,
    responses: {
      200: DeleteShipmentLegResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Delete a shipment leg and its associated resources"
  },
  cancelQueueEntry: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/cancel-queue",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: z3.object({}),
    responses: {
      200: CancelQueueEntryResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Cancel a queue entry without deleting the leg"
  },
  updateShipmentLeg: {
    method: "PATCH",
    path: "/api/supplier/shipment-legs/:legId",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: UpdateShipmentLegRequestSchema,
    responses: {
      200: UpdateShipmentLegResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      // Status conflict (e.g., trying to edit in_progress leg)
      500: errorResponse2
    },
    summary: "Update a shipment leg (only for planned/queued status)"
  },
  getManifest: {
    method: "GET",
    path: "/api/supplier/shipment-legs/:legId/manifest",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    responses: {
      200: GetShipmentLegManifestResponseSchema,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Get shipment-leg manifest detail"
  },
  prepareManifest: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/manifest/prepare",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: PrepareShipmentLegManifestRequestSchema,
    responses: {
      200: PrepareShipmentLegManifestResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Prepare shipment-leg manifest payload and readiness snapshot"
  },
  submitManifest: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/manifest/submit",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: SubmitShipmentLegManifestRequestSchema,
    responses: {
      200: ShipmentLegManifestActionResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Submit shipment-leg manifest to Metrc"
  },
  updateManifest: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/manifest/update",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: UpdateShipmentLegManifestRequestSchema,
    responses: {
      200: ShipmentLegManifestActionResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Update a submitted shipment-leg manifest in Metrc"
  },
  voidManifest: {
    method: "POST",
    path: "/api/supplier/shipment-legs/:legId/manifest/void",
    pathParams: z3.object({ legId: z3.string().uuid() }),
    body: VoidShipmentLegManifestRequestSchema,
    responses: {
      200: ShipmentLegManifestActionResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Void shipment-leg manifest in Metrc/local state"
  },
  // ── DAG Manifest Endpoints ──
  prepareShipmentManifests: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/manifests/prepare",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: PrepareShipmentManifestsRequestSchema,
    responses: {
      200: PrepareShipmentManifestsResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Prepare per-destination Metrc manifests for a shipment (DAG model)"
  },
  submitShipmentManifest: {
    method: "POST",
    path: "/api/supplier/manifests/:manifestId/submit",
    pathParams: z3.object({ manifestId: z3.string().uuid() }),
    body: SubmitShipmentManifestRequestSchema,
    responses: {
      200: ShipmentManifestActionResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Submit a per-destination manifest to Metrc"
  },
  updateShipmentManifest: {
    method: "POST",
    path: "/api/supplier/manifests/:manifestId/update",
    pathParams: z3.object({ manifestId: z3.string().uuid() }),
    body: SubmitShipmentManifestRequestSchema,
    responses: {
      200: ShipmentManifestActionResponseSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      409: errorResponse2,
      500: errorResponse2
    },
    summary: "Update a submitted per-destination manifest in Metrc"
  },
  dispatchShipment: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/dispatch",
    pathParams: z3.object({ shipmentId: z3.string().uuid() }),
    body: DispatchShipmentRequestSchema,
    responses: {
      200: DispatchShipmentResultSchema,
      400: errorResponse2,
      401: errorResponse2,
      403: errorResponse2,
      404: errorResponse2,
      500: errorResponse2
    },
    summary: "Dispatch a shipment to Onfleet for last-mile delivery execution"
  }
});

// src/http/delivery/supplier-route-plans.contract.ts
import { initContract as initContract3 } from "@ts-rest/core";
import { z as z4 } from "zod";
var c3 = initContract3();
var errorResponse3 = z4.object({ message: z4.string() });
var RoutePlanStateSchema = z4.enum(["pending", "in_progress", "completed"]);
var RoutePlanStatusSchema = z4.enum(["early", "on_time", "late", "unavailable"]);
var RoutePlanLocationOptionSchema = z4.enum(["HUB", "WORKER_LOCATION", "WORKER_ADDRESS"]).nullable();
var RoutePlanSchema = z4.object({
  id: z4.string().uuid(),
  onfleetRoutePlanId: z4.string(),
  name: z4.string(),
  state: RoutePlanStateSchema,
  status: RoutePlanStatusSchema,
  color: z4.string(),
  taskIds: z4.array(z4.string().uuid()),
  taskCount: z4.number(),
  driverId: z4.string().uuid().nullable(),
  driverName: z4.string().nullable(),
  teamId: z4.string().uuid().nullable(),
  organizationId: z4.string().uuid(),
  startTime: z4.string(),
  endTime: z4.string().nullable(),
  actualStartTime: z4.string().nullable(),
  actualEndTime: z4.string().nullable(),
  startAt: RoutePlanLocationOptionSchema,
  endAt: RoutePlanLocationOptionSchema,
  shortId: z4.string().nullable(),
  timezone: z4.string(),
  plannedDistance: z4.number(),
  plannedDuration: z4.number(),
  isOverdue: z4.boolean(),
  createdAt: z4.string(),
  updatedAt: z4.string()
});
var RoutePlanSummarySchema = z4.object({
  id: z4.string().uuid(),
  onfleetRoutePlanId: z4.string(),
  name: z4.string(),
  state: RoutePlanStateSchema,
  status: RoutePlanStatusSchema,
  color: z4.string(),
  taskCount: z4.number(),
  driverId: z4.string().uuid().nullable(),
  driverName: z4.string().nullable(),
  teamId: z4.string().uuid().nullable(),
  startTime: z4.string(),
  endTime: z4.string().nullable(),
  shortId: z4.string().nullable(),
  timezone: z4.string(),
  plannedDistance: z4.number(),
  plannedDuration: z4.number(),
  isOverdue: z4.boolean()
});
var CreateRoutePlanInputSchema = z4.object({
  name: z4.string().min(1).max(255),
  startTime: z4.number().positive(),
  taskIds: z4.array(z4.string().uuid()).optional(),
  color: z4.string().optional(),
  driverId: z4.string().uuid().optional(),
  teamId: z4.string().uuid().optional(),
  endTime: z4.number().positive().optional(),
  timezone: z4.string().optional(),
  startAt: RoutePlanLocationOptionSchema.optional(),
  endAt: RoutePlanLocationOptionSchema.optional()
});
var ListRoutePlansQuerySchema = z4.object({
  driverId: z4.string().uuid().optional(),
  startTimeFrom: z4.string().optional(),
  startTimeTo: z4.string().optional(),
  state: RoutePlanStateSchema.optional(),
  limit: z4.coerce.number().positive().max(100).optional()
});
var UpdateRoutePlanTasksInputSchema = z4.object({
  taskIds: z4.array(z4.string().uuid())
});
var supplierRoutePlansContract = c3.router({
  createRoutePlan: {
    method: "POST",
    path: "/api/supplier/route-plans",
    body: CreateRoutePlanInputSchema,
    responses: {
      201: RoutePlanSchema,
      400: errorResponse3,
      401: errorResponse3,
      403: errorResponse3,
      500: errorResponse3
    },
    summary: "Create a new route plan"
  },
  listRoutePlans: {
    method: "GET",
    path: "/api/supplier/route-plans",
    query: ListRoutePlansQuerySchema,
    responses: {
      200: z4.array(RoutePlanSummarySchema),
      401: errorResponse3,
      403: errorResponse3,
      500: errorResponse3
    },
    summary: "List route plans for the organization"
  },
  getRoutePlan: {
    method: "GET",
    path: "/api/supplier/route-plans/:routePlanId",
    pathParams: z4.object({ routePlanId: z4.string().uuid() }),
    responses: {
      200: RoutePlanSchema,
      401: errorResponse3,
      403: errorResponse3,
      404: errorResponse3,
      500: errorResponse3
    },
    summary: "Get a single route plan with task IDs"
  },
  updateRoutePlanTasks: {
    method: "PUT",
    path: "/api/supplier/route-plans/:routePlanId/tasks",
    pathParams: z4.object({ routePlanId: z4.string().uuid() }),
    body: UpdateRoutePlanTasksInputSchema,
    responses: {
      200: RoutePlanSchema,
      400: errorResponse3,
      401: errorResponse3,
      403: errorResponse3,
      404: errorResponse3,
      500: errorResponse3
    },
    summary: "Replace tasks in a route plan"
  },
  deleteRoutePlan: {
    method: "DELETE",
    path: "/api/supplier/route-plans/:routePlanId",
    pathParams: z4.object({ routePlanId: z4.string().uuid() }),
    body: c3.noBody(),
    responses: {
      204: c3.noBody(),
      401: errorResponse3,
      403: errorResponse3,
      404: errorResponse3,
      500: errorResponse3
    },
    summary: "Delete a route plan"
  }
});

// src/http/delivery/auxiliary-tasks.contract.ts
import { initContract as initContract4 } from "@ts-rest/core";
import { z as z5 } from "zod";
import {
  ShipmentAuxiliaryTaskTypeSchema,
  AuxiliaryTaskAddressSchema
} from "@flote/types/delivery";
var c4 = initContract4();
var errorResponse4 = z5.object({ message: z5.string() });
var CreateAuxiliaryTaskBodySchema = z5.object({
  legId: z5.string().uuid(),
  taskType: ShipmentAuxiliaryTaskTypeSchema,
  description: z5.string().min(1),
  address: AuxiliaryTaskAddressSchema,
  recipientName: z5.string().optional(),
  recipientPhone: z5.string().optional(),
  notes: z5.string().optional()
});
var CreateAuxiliaryTaskResponseSchema = z5.object({
  auxiliaryTaskId: z5.string().uuid(),
  onfleetTaskId: z5.string()
});
var auxiliaryTasksContract = c4.router({
  create: {
    method: "POST",
    path: "/api/supplier/shipments/:shipmentId/auxiliary-tasks",
    pathParams: z5.object({ shipmentId: z5.string().uuid() }),
    body: CreateAuxiliaryTaskBodySchema,
    responses: {
      201: CreateAuxiliaryTaskResponseSchema,
      400: errorResponse4,
      404: errorResponse4,
      500: errorResponse4
    },
    summary: "Create an operational auxiliary task on a shipment leg"
  },
  remove: {
    method: "DELETE",
    path: "/api/supplier/shipments/:shipmentId/auxiliary-tasks/:taskId",
    pathParams: z5.object({
      shipmentId: z5.string().uuid(),
      taskId: z5.string().uuid()
    }),
    body: c4.noBody(),
    responses: {
      200: z5.object({ success: z5.boolean() }),
      404: errorResponse4,
      500: errorResponse4
    },
    summary: "Remove an auxiliary task from a shipment"
  }
});

// src/http/delivery/supplier-drivers.contract.ts
import { initContract as initContract5 } from "@ts-rest/core";
import { z as z6 } from "zod";
var c5 = initContract5();
var errorResponse5 = z6.object({ message: z6.string() });
var CreateSupplierDriverInputSchema = z6.object({
  name: z6.string().min(1, "Driver name is required"),
  phone: z6.string().min(1, "Phone number is required"),
  locationTeamIds: z6.array(z6.string().uuid()).min(1, "At least one location team is required"),
  metrcEmployeeId: z6.string().min(1, "Metrc employee ID is required"),
  driversLicenseNumber: z6.string().min(1, "Driver's license number is required"),
  displayName: z6.string().optional(),
  capacity: z6.number().int().positive().optional(),
  vehicleType: VehicleTypeSchema.optional(),
  vehicleDescription: z6.string().optional(),
  vehicleLicensePlate: z6.string().optional(),
  vehicleColor: z6.string().optional()
});
var UpdateSupplierDriverInputSchema = z6.object({
  displayName: z6.string().optional(),
  metrcEmployeeId: z6.string().nullable().optional(),
  driversLicenseNumber: z6.string().nullable().optional(),
  capacity: z6.number().int().positive().optional(),
  vehicleType: VehicleTypeSchema.nullable().optional(),
  vehicleDescription: z6.string().nullable().optional(),
  vehicleLicensePlate: z6.string().nullable().optional(),
  vehicleColor: z6.string().nullable().optional()
});
var UpdateDriverTeamsInputSchema = z6.object({
  teamIds: z6.array(z6.string().uuid()).min(1, "At least one team is required")
});
var ListSupplierDriversQuerySchema = z6.object({
  status: DriverStatusSchema.optional(),
  onDuty: z6.coerce.boolean().optional(),
  search: z6.string().optional(),
  stockLocationId: z6.string().uuid().optional(),
  limit: z6.coerce.number().default(50)
});
var DriverTasksQuerySchema = z6.object({
  status: DeliveryTaskStatusSchema.optional(),
  limit: z6.coerce.number().default(50)
});
var TaskAssignmentResultSchema = z6.object({
  taskId: z6.string().uuid(),
  onfleetTaskId: z6.string(),
  trackingId: z6.string()
});
var AssignDriverToPartitionRequestSchema = z6.object({
  driverId: z6.string().uuid(),
  teamId: z6.string().uuid().optional()
});
var PhotoUploadResponseSchema = z6.object({
  photoUrl: z6.string().url()
});
var DriverAnalyticsQuerySchema = z6.object({
  from: z6.coerce.number().optional(),
  to: z6.coerce.number().optional()
});
var DriverAnalyticsSchema = z6.object({
  taskCounts: z6.object({
    succeeded: z6.number(),
    failed: z6.number()
  }),
  times: z6.object({
    enroute: z6.number(),
    idle: z6.number()
  }),
  distances: z6.object({
    enroute: z6.number(),
    idle: z6.number()
  }),
  period: z6.object({
    from: z6.string().datetime(),
    to: z6.string().datetime()
  })
});
var DriverPoolQuerySchema = z6.object({
  includeUnavailable: z6.coerce.boolean().default(false)
});
var DriverPoolResponseSchema = z6.object({
  drivers: z6.array(DriverSchema),
  location: z6.object({
    id: z6.string(),
    name: z6.string()
  }),
  team: z6.object({
    id: z6.string(),
    onfleetTeamId: z6.string()
  }),
  totalDriversAtLocation: z6.number().int(),
  availableCount: z6.number().int()
});
var ActiveDeliveryTaskSummarySchema = z6.object({
  id: z6.string(),
  status: DeliveryTaskStatusSchema,
  recipientName: z6.string(),
  taskType: z6.enum(["pickup", "dropoff"]),
  scheduledEnd: z6.string().optional(),
  estimatedArrival: z6.string().optional()
});
var ActiveDeliverySessionSchema = z6.object({
  driverId: z6.string().uuid(),
  driverName: z6.string(),
  driverPhotoUrl: z6.string().optional(),
  taskCount: z6.number().int(),
  startedAt: z6.string(),
  latestStatus: z6.enum(["assigned", "in_transit", "arrived"]),
  isOverdue: z6.boolean(),
  timingStatus: z6.enum(["on_time", "late"]),
  tasks: z6.array(ActiveDeliveryTaskSummarySchema)
});
var supplierDriversContract = c5.router({
  // List drivers for the authenticated supplier
  listDrivers: {
    method: "GET",
    path: "/api/supplier/drivers",
    query: ListSupplierDriversQuerySchema,
    responses: {
      200: z6.array(DriverSchema),
      401: errorResponse5,
      403: errorResponse5,
      500: errorResponse5
    },
    summary: "List supplier's drivers"
  },
  // Get available (idle + on_duty) drivers for assignment
  getAvailableDrivers: {
    method: "GET",
    path: "/api/supplier/drivers/available",
    responses: {
      200: z6.array(DriverSchema),
      401: errorResponse5,
      403: errorResponse5,
      500: errorResponse5
    },
    summary: "Get available drivers for task assignment"
  },
  // Get single driver by ID
  getDriver: {
    method: "GET",
    path: "/api/supplier/drivers/:driverId",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    responses: {
      200: DriverSchema,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Get driver by ID"
  },
  // Create a new driver
  createDriver: {
    method: "POST",
    path: "/api/supplier/drivers",
    body: CreateSupplierDriverInputSchema,
    responses: {
      201: DriverSchema,
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      500: errorResponse5
    },
    summary: "Create a new driver for the supplier"
  },
  // Update a driver
  updateDriver: {
    method: "PATCH",
    path: "/api/supplier/drivers/:driverId",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    body: UpdateSupplierDriverInputSchema,
    responses: {
      200: DriverSchema,
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Update a driver"
  },
  // Update driver team membership
  updateDriverTeams: {
    method: "PATCH",
    path: "/api/supplier/drivers/:driverId/teams",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    body: UpdateDriverTeamsInputSchema,
    responses: {
      200: DriverSchema,
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Update driver team membership"
  },
  // Delete a driver
  deleteDriver: {
    method: "DELETE",
    path: "/api/supplier/drivers/:driverId",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    body: z6.object({}),
    responses: {
      204: z6.undefined(),
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Delete a driver (fails if driver has active tasks)"
  },
  // Get driver's tasks
  getDriverTasks: {
    method: "GET",
    path: "/api/supplier/drivers/:driverId/tasks",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    query: DriverTasksQuerySchema,
    responses: {
      200: z6.array(DeliveryTaskSchema),
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Get driver's assigned tasks"
  },
  // Get driver analytics
  getDriverAnalytics: {
    method: "GET",
    path: "/api/supplier/drivers/:driverId/analytics",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    query: DriverAnalyticsQuerySchema,
    responses: {
      200: DriverAnalyticsSchema,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Get driver analytics (task counts, times, distances)"
  },
  // Upload driver photo
  uploadDriverPhoto: {
    method: "POST",
    path: "/api/supplier/drivers/:driverId/photo",
    pathParams: z6.object({ driverId: z6.string().uuid() }),
    contentType: "multipart/form-data",
    body: c5.type(),
    responses: {
      200: PhotoUploadResponseSchema,
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Upload driver photo"
  },
  // Get driver pool for a partition (drivers at partition's stock location)
  getDriverPool: {
    method: "GET",
    path: "/api/supplier/partitions/:partitionId/driver-pool",
    pathParams: z6.object({ partitionId: z6.string().uuid() }),
    query: DriverPoolQuerySchema,
    responses: {
      200: DriverPoolResponseSchema,
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      500: errorResponse5
    },
    summary: "Get drivers available at the partition stock location"
  },
  // Get active delivery sessions (tasks grouped by driver)
  getActiveDeliveries: {
    method: "GET",
    path: "/api/supplier/active-deliveries",
    responses: {
      200: z6.array(ActiveDeliverySessionSchema),
      401: errorResponse5,
      403: errorResponse5,
      500: errorResponse5
    },
    summary: "Get active delivery sessions grouped by driver"
  },
  // Assign driver to a partition (updates existing Onfleet task)
  assignToPartition: {
    method: "POST",
    path: "/api/supplier/partitions/:partitionId/assign-driver",
    pathParams: z6.object({ partitionId: z6.string().uuid() }),
    body: AssignDriverToPartitionRequestSchema,
    responses: {
      200: TaskAssignmentResultSchema,
      400: errorResponse5,
      401: errorResponse5,
      403: errorResponse5,
      404: errorResponse5,
      409: errorResponse5,
      // Partition already assigned or not in assignable state
      500: errorResponse5
    },
    summary: "Assign a driver to an order partition"
  }
});

// src/http/delivery/supplier-delivery-team.contract.ts
import { initContract as initContract6 } from "@ts-rest/core";
import { z as z7 } from "zod";
var c6 = initContract6();
var errorResponse6 = z7.object({ message: z7.string() });
var SupplierLocationTeamSchema = z7.object({
  id: z7.string().uuid(),
  supplierId: z7.string().uuid(),
  stockLocationId: z7.string().uuid(),
  teamId: z7.string().uuid(),
  onfleetTeamId: z7.string(),
  onfleetHubId: z7.string().nullable(),
  isActive: z7.boolean(),
  createdAt: z7.string().datetime(),
  updatedAt: z7.string().datetime()
});
var SupplierLocationTeamWithDetailsSchema = SupplierLocationTeamSchema.extend({
  stockLocationName: z7.string(),
  stockLocationAddress: z7.object({
    address1: z7.string(),
    city: z7.string().nullable(),
    province: z7.string().nullable(),
    postalCode: z7.string().nullable(),
    locationLat: z7.string().nullable(),
    locationLng: z7.string().nullable()
  }).nullable(),
  teamName: z7.string(),
  driverCount: z7.number(),
  taskCount: z7.number(),
  canEdit: z7.boolean(),
  canView: z7.boolean(),
  supplierCount: z7.number()
});
var CreateSupplierLocationTeamRequestSchema = z7.object({
  stockLocationId: z7.string().uuid(),
  creatorEmail: z7.string().email().optional(),
  creatorName: z7.string().min(1).optional()
});
var SupplierDeliveryTeamSchema = TeamSchema.extend({
  hubName: z7.string().nullable(),
  hubStockLocationId: z7.string().uuid().nullable()
});
var UpdateSupplierDeliveryTeamRequestSchema = z7.object({
  name: z7.string().min(1).max(100).optional(),
  enableSelfAssignment: z7.boolean().optional(),
  hubStockLocationId: z7.string().uuid().nullable().optional()
});
var DeliveryTeamHubOptionSchema = z7.object({
  stockLocationId: z7.string().uuid(),
  name: z7.string(),
  address: z7.object({
    address1: z7.string(),
    city: z7.string().nullable(),
    province: z7.string().nullable(),
    postalCode: z7.string().nullable()
  }),
  isCurrentHub: z7.boolean(),
  hasOnfleetHub: z7.boolean()
});
var DeliveryTeamManagerSchema = z7.object({
  id: z7.string(),
  onfleetAdminId: z7.string(),
  name: z7.string(),
  email: z7.string().email(),
  phone: z7.string().nullable(),
  type: z7.enum(["super", "standard"]),
  isActive: z7.boolean()
});
var AddDeliveryTeamManagerRequestSchema = z7.object({
  onfleetAdminId: z7.string()
});
var supplierDeliveryTeamContract = c6.router({
  // Get supplier's delivery team details
  getDeliveryTeam: {
    method: "GET",
    path: "/api/supplier/delivery-team",
    responses: {
      200: SupplierDeliveryTeamSchema,
      401: errorResponse6,
      403: errorResponse6,
      404: errorResponse6,
      500: errorResponse6
    },
    summary: "Get supplier's delivery team details"
  },
  // Update delivery team settings
  updateDeliveryTeam: {
    method: "PATCH",
    path: "/api/supplier/delivery-team",
    body: UpdateSupplierDeliveryTeamRequestSchema,
    responses: {
      200: SupplierDeliveryTeamSchema,
      400: errorResponse6,
      401: errorResponse6,
      403: errorResponse6,
      404: errorResponse6,
      500: errorResponse6
    },
    summary: "Update delivery team settings (name, self-assignment, hub)"
  },
  // List hub options (stock locations supplier can assign as hub)
  listDeliveryTeamHubOptions: {
    method: "GET",
    path: "/api/supplier/delivery-team/hub-options",
    responses: {
      200: z7.array(DeliveryTeamHubOptionSchema),
      401: errorResponse6,
      403: errorResponse6,
      500: errorResponse6
    },
    summary: "List available hub options from supplier stock locations"
  },
  // List managers assigned to delivery team
  listDeliveryTeamManagers: {
    method: "GET",
    path: "/api/supplier/delivery-team/managers",
    responses: {
      200: z7.array(DeliveryTeamManagerSchema),
      401: errorResponse6,
      403: errorResponse6,
      404: errorResponse6,
      500: errorResponse6
    },
    summary: "List managers assigned to supplier delivery team"
  },
  // List available managers (org admins not yet on team)
  listAvailableDeliveryTeamManagers: {
    method: "GET",
    path: "/api/supplier/delivery-team/managers/available",
    responses: {
      200: z7.array(DeliveryTeamManagerSchema),
      401: errorResponse6,
      403: errorResponse6,
      500: errorResponse6
    },
    summary: "List org admins available to add to delivery team"
  },
  // Add manager to delivery team
  addDeliveryTeamManager: {
    method: "POST",
    path: "/api/supplier/delivery-team/managers",
    body: AddDeliveryTeamManagerRequestSchema,
    responses: {
      201: DeliveryTeamManagerSchema,
      400: errorResponse6,
      401: errorResponse6,
      403: errorResponse6,
      404: errorResponse6,
      409: errorResponse6,
      // Already on team
      500: errorResponse6
    },
    summary: "Add existing org admin to supplier delivery team"
  },
  // Remove manager from delivery team
  removeDeliveryTeamManager: {
    method: "DELETE",
    path: "/api/supplier/delivery-team/managers/:managerId",
    pathParams: z7.object({
      managerId: z7.string()
    }),
    body: z7.object({}),
    responses: {
      204: z7.undefined(),
      401: errorResponse6,
      403: errorResponse6,
      404: errorResponse6,
      500: errorResponse6
    },
    summary: "Remove manager from supplier delivery team"
  },
  // ================================================================
  // Multi-Location Team Management
  // ================================================================
  // List all delivery teams across stock locations
  listLocationTeams: {
    method: "GET",
    path: "/api/supplier/delivery-teams",
    responses: {
      200: z7.array(SupplierLocationTeamWithDetailsSchema),
      401: errorResponse6,
      403: errorResponse6,
      500: errorResponse6
    },
    summary: "List all delivery teams for supplier, organized by stock location"
  },
  // Create a delivery team for a specific stock location
  createLocationTeam: {
    method: "POST",
    path: "/api/supplier/delivery-teams",
    body: CreateSupplierLocationTeamRequestSchema,
    responses: {
      201: SupplierLocationTeamSchema,
      400: errorResponse6,
      401: errorResponse6,
      403: errorResponse6,
      409: errorResponse6,
      500: errorResponse6
    },
    summary: "Create a delivery team for a stock location (explicit CTA)"
  },
  // Get a specific location team
  getLocationTeam: {
    method: "GET",
    path: "/api/supplier/delivery-teams/:locationTeamId",
    pathParams: z7.object({
      locationTeamId: z7.string().uuid()
    }),
    responses: {
      200: SupplierLocationTeamWithDetailsSchema,
      401: errorResponse6,
      403: errorResponse6,
      404: errorResponse6,
      500: errorResponse6
    },
    summary: "Get details of a specific location team"
  }
});

// src/http/delivery/supplier-vehicles.contract.ts
import { initContract as initContract7 } from "@ts-rest/core";
import { z as z8 } from "zod";
var VehicleStatusSchema = z8.enum(["available", "assigned", "maintenance", "retired"]);
var VehicleSchema = z8.object({
  id: z8.string(),
  type: VehicleTypeSchema,
  description: z8.string().optional(),
  make: z8.string().optional(),
  model: z8.string().optional(),
  licensePlate: z8.string().optional(),
  color: z8.string().optional(),
  status: VehicleStatusSchema,
  teamId: z8.string().optional(),
  capacity: z8.number().optional(),
  notes: z8.string().optional(),
  createdAt: z8.string().datetime(),
  updatedAt: z8.string().datetime()
});
var c7 = initContract7();
var errorResponse7 = z8.object({ message: z8.string() });
var VEHICLE_TYPES_REQUIRING_MAKE_MODEL = ["CAR", "TRUCK", "MOTORCYCLE"];
function isNonEmpty(value) {
  return typeof value === "string" && value.trim().length > 0;
}
function vehicleTypeRequiresMakeAndModel(type) {
  return typeof type === "string" && VEHICLE_TYPES_REQUIRING_MAKE_MODEL.includes(type);
}
var CreateSupplierVehicleInputSchema = z8.object({
  type: VehicleTypeSchema,
  description: z8.string().optional(),
  make: z8.string().optional(),
  model: z8.string().optional(),
  licensePlate: z8.string().optional(),
  color: z8.string().optional(),
  capacity: z8.number().int().positive().optional(),
  notes: z8.string().optional()
}).superRefine((data, ctx) => {
  if (!vehicleTypeRequiresMakeAndModel(data.type)) return;
  if (!isNonEmpty(data.make)) {
    ctx.addIssue({ code: z8.ZodIssueCode.custom, message: "Make is required for motorized vehicles.", path: ["make"] });
  }
  if (!isNonEmpty(data.model)) {
    ctx.addIssue({ code: z8.ZodIssueCode.custom, message: "Model is required for motorized vehicles.", path: ["model"] });
  }
});
var UpdateSupplierVehicleInputSchema = z8.object({
  type: VehicleTypeSchema.optional(),
  description: z8.string().nullable().optional(),
  make: z8.string().nullable().optional(),
  model: z8.string().nullable().optional(),
  licensePlate: z8.string().nullable().optional(),
  color: z8.string().nullable().optional(),
  status: VehicleStatusSchema.optional(),
  capacity: z8.number().int().positive().nullable().optional(),
  notes: z8.string().nullable().optional()
}).superRefine((data, ctx) => {
  if (!vehicleTypeRequiresMakeAndModel(data.type)) return;
  if (data.make === null || data.make !== void 0 && !isNonEmpty(data.make)) {
    ctx.addIssue({ code: z8.ZodIssueCode.custom, message: "Make cannot be cleared for motorized vehicles.", path: ["make"] });
  }
  if (data.model === null || data.model !== void 0 && !isNonEmpty(data.model)) {
    ctx.addIssue({ code: z8.ZodIssueCode.custom, message: "Model cannot be cleared for motorized vehicles.", path: ["model"] });
  }
});
var ListSupplierVehiclesQuerySchema = z8.object({
  status: VehicleStatusSchema.optional(),
  type: VehicleTypeSchema.optional(),
  limit: z8.coerce.number().default(100)
});
var AssignVehicleToDriverRequestSchema = z8.object({
  driverId: z8.string().uuid()
});
var supplierVehiclesContract = c7.router({
  // List vehicles for the authenticated supplier's team
  listVehicles: {
    method: "GET",
    path: "/api/supplier/vehicles",
    query: ListSupplierVehiclesQuerySchema,
    responses: {
      200: z8.array(VehicleSchema),
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      // Supplier has no delivery team
      500: errorResponse7
    },
    summary: "List supplier's fleet vehicles"
  },
  // Get available vehicles for assignment
  getAvailableVehicles: {
    method: "GET",
    path: "/api/supplier/vehicles/available",
    responses: {
      200: z8.array(VehicleSchema),
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      500: errorResponse7
    },
    summary: "Get available vehicles for driver assignment"
  },
  // Get single vehicle by ID
  getVehicle: {
    method: "GET",
    path: "/api/supplier/vehicles/:vehicleId",
    pathParams: z8.object({ vehicleId: z8.string().uuid() }),
    responses: {
      200: VehicleSchema,
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      500: errorResponse7
    },
    summary: "Get vehicle by ID"
  },
  // Create a new vehicle for supplier's fleet
  createVehicle: {
    method: "POST",
    path: "/api/supplier/vehicles",
    body: CreateSupplierVehicleInputSchema,
    responses: {
      201: VehicleSchema,
      400: errorResponse7,
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      // Supplier has no delivery team
      500: errorResponse7
    },
    summary: "Create a new vehicle for the supplier's fleet"
  },
  // Update a vehicle
  updateVehicle: {
    method: "PATCH",
    path: "/api/supplier/vehicles/:vehicleId",
    pathParams: z8.object({ vehicleId: z8.string().uuid() }),
    body: UpdateSupplierVehicleInputSchema,
    responses: {
      200: VehicleSchema,
      400: errorResponse7,
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      500: errorResponse7
    },
    summary: "Update a vehicle"
  },
  // Delete a vehicle
  deleteVehicle: {
    method: "DELETE",
    path: "/api/supplier/vehicles/:vehicleId",
    pathParams: z8.object({ vehicleId: z8.string().uuid() }),
    body: z8.object({}),
    responses: {
      204: z8.undefined(),
      400: errorResponse7,
      // Vehicle is assigned to driver
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      500: errorResponse7
    },
    summary: "Delete a vehicle (fails if assigned to a driver)"
  },
  // Assign vehicle to a driver
  assignToDriver: {
    method: "POST",
    path: "/api/supplier/vehicles/:vehicleId/assign",
    pathParams: z8.object({ vehicleId: z8.string().uuid() }),
    body: AssignVehicleToDriverRequestSchema,
    responses: {
      200: VehicleSchema,
      400: errorResponse7,
      // Vehicle not available or driver not found
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      500: errorResponse7
    },
    summary: "Assign a vehicle to a driver"
  },
  // Unassign vehicle from driver
  unassignFromDriver: {
    method: "POST",
    path: "/api/supplier/vehicles/:vehicleId/unassign",
    pathParams: z8.object({ vehicleId: z8.string().uuid() }),
    body: z8.object({}),
    responses: {
      200: VehicleSchema,
      400: errorResponse7,
      // Vehicle is not assigned
      401: errorResponse7,
      403: errorResponse7,
      404: errorResponse7,
      500: errorResponse7
    },
    summary: "Unassign a vehicle from its driver"
  }
});

// src/http/delivery/supplier-routes.contract.ts
import { initContract as initContract8 } from "@ts-rest/core";
import { z as z9 } from "zod";
var c8 = initContract8();
var errorResponse8 = z9.object({ message: z9.string() });
var RoutePointSchema = z9.object({
  lat: z9.number(),
  lng: z9.number(),
  capturedAt: z9.string().datetime()
});
var GeoLocationSchema = z9.object({
  lat: z9.number(),
  lng: z9.number()
});
var TaskRouteSchema = z9.object({
  taskId: z9.string(),
  trackingId: z9.string(),
  driverId: z9.string(),
  driverName: z9.string(),
  status: DeliveryTaskStatusSchema,
  startPoint: GeoLocationSchema.optional(),
  endPoint: GeoLocationSchema,
  routePoints: z9.array(RoutePointSchema),
  totalPoints: z9.number(),
  completedAt: z9.string().datetime().optional(),
  failureReason: z9.string().optional(),
  failureNotes: z9.string().optional(),
  recipientName: z9.string(),
  recipientAddress: z9.string()
});
var RouteBoundsSchema = z9.object({
  sw: GeoLocationSchema,
  ne: GeoLocationSchema
});
var BatchRoutesResponseSchema = z9.object({
  routes: z9.array(TaskRouteSchema),
  bounds: RouteBoundsSchema.optional()
});
var GetSupplierRoutesQuerySchema = z9.object({
  driverId: z9.string().uuid().optional(),
  status: DeliveryTaskStatusSchema.optional(),
  fromDate: z9.string().optional(),
  toDate: z9.string().optional(),
  simplify: z9.coerce.boolean().default(true),
  simplifyTolerance: z9.coerce.number().default(1e-4)
});
var supplierRoutesContract = c8.router({
  // Get batch routes for the supplier's team
  getBatchRoutes: {
    method: "GET",
    path: "/api/supplier/routes",
    query: GetSupplierRoutesQuerySchema,
    responses: {
      200: BatchRoutesResponseSchema,
      400: errorResponse8,
      401: errorResponse8,
      403: errorResponse8,
      404: errorResponse8,
      // Supplier has no delivery team
      500: errorResponse8
    },
    summary: "Get historical GPS routes for the supplier's drivers"
  },
  // Get single route by task ID
  getTaskRoute: {
    method: "GET",
    path: "/api/supplier/routes/:taskId",
    pathParams: z9.object({ taskId: z9.string().uuid() }),
    query: z9.object({
      simplify: z9.coerce.boolean().default(true)
    }),
    responses: {
      200: TaskRouteSchema,
      401: errorResponse8,
      403: errorResponse8,
      404: errorResponse8,
      500: errorResponse8
    },
    summary: "Get route data for a single task"
  }
});

// src/http/delivery/index.ts
var c9 = initContract9();
var errorResponse9 = z10.object({ message: z10.string() });
var DashboardStatsSchema = z10.object({
  totalTasks: z10.number(),
  pendingTasks: z10.number(),
  inTransitTasks: z10.number(),
  completedToday: z10.number(),
  failedToday: z10.number(),
  activeDrivers: z10.number(),
  totalDrivers: z10.number()
});
var AuthValidationResponseSchema = z10.object({
  valid: z10.boolean(),
  message: z10.string().optional()
});
var adminDashboardContract = c9.router({
  getStats: {
    method: "GET",
    path: "/api/admin/dashboard/stats",
    responses: {
      200: DashboardStatsSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get dashboard statistics"
  }
});
var SyncResponseSchema = z10.object({
  synced: z10.number(),
  message: z10.string().optional()
});
var PublicTrackingResponseSchema = z10.object({
  trackingId: z10.string(),
  status: DeliveryTaskStatusSchema,
  recipientName: z10.string(),
  address: z10.object({
    city: z10.string(),
    state: z10.string().optional()
  }),
  estimatedArrival: z10.string().datetime().optional(),
  driver: z10.object({
    name: z10.string(),
    photoUrl: z10.string().optional(),
    location: z10.object({
      lat: z10.number(),
      lng: z10.number()
    }).optional()
  }).optional(),
  timeline: z10.array(z10.object({
    status: DeliveryTaskStatusSchema,
    timestamp: z10.string().datetime(),
    message: z10.string().optional()
  }))
});
var ListTasksQuerySchema = z10.object({
  status: DeliveryTaskStatusSchema.optional(),
  taskType: DeliveryTaskTypeSchema.optional(),
  driverId: z10.string().optional(),
  fromDate: z10.string().optional(),
  toDate: z10.string().optional(),
  limit: z10.coerce.number().default(50),
  cursor: z10.string().optional()
});
var ListDriversQuerySchema = z10.object({
  status: DriverStatusSchema.optional(),
  onDuty: z10.coerce.boolean().optional(),
  teamId: z10.string().optional(),
  limit: z10.coerce.number().default(50)
});
var VehicleTypeSchema2 = z10.enum(["CAR", "MOTORCYCLE", "BICYCLE", "TRUCK"]);
var VehicleStatusSchema2 = z10.enum(["available", "assigned", "maintenance", "retired"]);
var CreateDriverVehicleInputSchema = z10.object({
  type: VehicleTypeSchema2,
  description: z10.string().optional(),
  licensePlate: z10.string().optional(),
  color: z10.string().optional()
});
var CreateDriverRequestSchema = z10.object({
  name: z10.string().min(1, "Name is required"),
  phone: z10.string().min(1, "Phone is required"),
  teams: z10.array(z10.string()).min(1, "At least one team is required"),
  metrcEmployeeId: z10.string().optional(),
  driversLicenseNumber: z10.string().optional(),
  vehicle: CreateDriverVehicleInputSchema.nullable().optional(),
  vehicleId: z10.string().nullable().optional(),
  capacity: z10.number().optional(),
  displayName: z10.string().optional()
});
var UpdateDriverRequestSchema = z10.object({
  displayName: z10.string().optional(),
  teams: z10.array(z10.string()).optional(),
  metrcEmployeeId: z10.string().nullable().optional(),
  driversLicenseNumber: z10.string().nullable().optional(),
  vehicle: CreateDriverVehicleInputSchema.nullable().optional(),
  vehicleId: z10.string().nullable().optional(),
  capacity: z10.number().optional()
});
var CompletionRequirementsSchema = z10.object({
  signature: z10.boolean().optional(),
  photo: z10.boolean().optional(),
  notes: z10.boolean().optional(),
  minimumAge: z10.number().optional()
});
var TaskAddressSchema = z10.object({
  number: z10.string(),
  street: z10.string(),
  apartment: z10.string().optional(),
  city: z10.string(),
  state: z10.string().optional(),
  postalCode: z10.string().optional(),
  country: z10.string().default("USA")
});
var CreateTaskRequestSchema = z10.object({
  taskType: DeliveryTaskTypeSchema.optional().default("dropoff"),
  // Default: 'dropoff' for backward compatibility
  orderId: z10.number().optional(),
  customerId: z10.string().optional(),
  teamId: z10.string().optional(),
  driverId: z10.string().optional(),
  recipientName: z10.string(),
  recipientPhone: z10.string().optional(),
  address: TaskAddressSchema,
  notes: z10.string().optional(),
  completeAfter: z10.string().datetime().optional(),
  completeBefore: z10.string().datetime().optional(),
  autoAssign: z10.boolean().default(false),
  completionRequirements: CompletionRequirementsSchema.optional()
});
var CreateLinkedTasksRequestSchema = z10.object({
  pickup: z10.object({
    address: TaskAddressSchema,
    recipientName: z10.string().min(1),
    // Supplier/pickup contact
    recipientPhone: z10.string().optional(),
    notes: z10.string().optional(),
    completeAfter: z10.string().datetime().optional(),
    // Pickup time window start
    completeBefore: z10.string().datetime().optional(),
    // Pickup time window end
    completionRequirements: CompletionRequirementsSchema.optional()
  }),
  dropoff: z10.object({
    orderId: z10.number().optional(),
    // B2BWave order
    customerId: z10.string().optional(),
    address: TaskAddressSchema,
    recipientName: z10.string().min(1),
    // Customer
    recipientPhone: z10.string().optional(),
    notes: z10.string().optional(),
    completeAfter: z10.string().datetime().optional(),
    // Dropoff time window start
    completeBefore: z10.string().datetime().optional(),
    // Dropoff time window end
    completionRequirements: CompletionRequirementsSchema.optional()
  }),
  teamId: z10.string().optional(),
  // Shared team assignment
  driverId: z10.string().optional(),
  // Shared driver assignment
  autoAssign: z10.boolean().optional(),
  metadata: z10.record(z10.unknown()).optional()
});
var LinkedTasksResponseSchema = z10.object({
  pickup: DeliveryTaskSchema,
  dropoff: DeliveryTaskSchema,
  trackingId: z10.string()
});
var UpdateTaskRequestSchema = z10.object({
  notes: z10.string().optional(),
  completeAfter: z10.string().datetime().optional(),
  completeBefore: z10.string().datetime().optional(),
  teamId: z10.string().optional()
});
var AssignTaskRequestSchema = z10.object({
  driverId: z10.string()
});
var TimelineEntrySchema = z10.object({
  status: DeliveryTaskStatusSchema,
  timestamp: z10.string().datetime(),
  message: z10.string().optional()
});
var paginatedResponse = (itemSchema) => z10.object({
  items: z10.array(itemSchema),
  nextCursor: z10.string().optional(),
  totalCount: z10.number().optional()
});
var CreateTeamRequestSchema = z10.object({
  name: z10.string().min(1, "Team name is required"),
  workers: z10.array(z10.string()).default([]),
  managers: z10.array(z10.string()).default([]),
  hub: z10.string().optional(),
  enableSelfAssignment: z10.boolean().optional()
});
var UpdateTeamRequestSchema = z10.object({
  name: z10.string().optional(),
  workers: z10.array(z10.string()).optional(),
  managers: z10.array(z10.string()).optional(),
  hub: z10.string().optional(),
  enableSelfAssignment: z10.boolean().optional()
});
var TeamMembersSchema = z10.object({
  teamId: z10.string(),
  workerIds: z10.array(z10.string()),
  managerIds: z10.array(z10.string())
});
var TeamAutoDispatchRequestSchema = z10.object({
  maxTasksPerRoute: z10.number().optional(),
  taskTimeWindow: z10.tuple([z10.number(), z10.number()]).optional(),
  scheduleTimeWindow: z10.tuple([z10.number(), z10.number()]).optional(),
  serviceTime: z10.number().optional(),
  routeEnd: z10.string().nullable().optional(),
  maxAllowedDelay: z10.number().optional()
});
var TeamAutoDispatchResponseSchema = z10.object({
  dispatchId: z10.string()
});
var HubAddressSchema = z10.object({
  number: z10.string().optional(),
  street: z10.string().optional(),
  apartment: z10.string().optional(),
  city: z10.string().optional(),
  state: z10.string().optional(),
  postalCode: z10.string().optional(),
  country: z10.string().optional()
});
var HubLocationSchema = z10.object({
  lat: z10.number(),
  lng: z10.number()
});
var HubSchema = z10.object({
  id: z10.string(),
  name: z10.string(),
  location: HubLocationSchema,
  address: HubAddressSchema,
  teams: z10.array(z10.string())
});
var CreateHubAddressSchema = z10.object({
  number: z10.string().optional(),
  street: z10.string().optional(),
  apartment: z10.string().optional(),
  city: z10.string().optional(),
  state: z10.string().optional(),
  postalCode: z10.string().optional(),
  country: z10.string().optional(),
  unparsed: z10.string().optional()
});
var CreateHubRequestSchema = z10.object({
  name: z10.string().min(1, "Hub name is required"),
  address: CreateHubAddressSchema,
  teams: z10.array(z10.string()).optional()
});
var UpdateHubRequestSchema = z10.object({
  name: z10.string().optional(),
  address: CreateHubAddressSchema.optional(),
  teams: z10.array(z10.string()).optional()
});
var trackingContract = c9.router({
  getByTrackingId: {
    method: "GET",
    path: "/api/tracking/:trackingId",
    pathParams: z10.object({ trackingId: z10.string() }),
    responses: {
      200: PublicTrackingResponseSchema,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get public tracking info by tracking ID"
  }
});
var adminAuthContract = c9.router({
  validate: {
    method: "POST",
    path: "/api/admin/auth/validate",
    body: z10.object({}),
    // Empty body, auth is via Bearer token
    responses: {
      200: AuthValidationResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Validate admin API key authentication"
  }
});
var adminTasksContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/tasks",
    query: ListTasksQuerySchema,
    responses: {
      200: paginatedResponse(DeliveryTaskSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List delivery tasks with filters"
  },
  getById: {
    method: "GET",
    path: "/api/admin/tasks/:taskId",
    pathParams: z10.object({ taskId: z10.string() }),
    responses: {
      200: DeliveryTaskSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get delivery task by ID"
  },
  create: {
    method: "POST",
    path: "/api/admin/tasks",
    body: CreateTaskRequestSchema,
    responses: {
      201: DeliveryTaskSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new delivery task"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/tasks/:taskId",
    pathParams: z10.object({ taskId: z10.string() }),
    body: UpdateTaskRequestSchema,
    responses: {
      200: DeliveryTaskSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update a delivery task"
  },
  assign: {
    method: "POST",
    path: "/api/admin/tasks/:taskId/assign",
    pathParams: z10.object({ taskId: z10.string() }),
    body: AssignTaskRequestSchema,
    responses: {
      200: DeliveryTaskSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Assign a task to a driver"
  },
  unassign: {
    method: "POST",
    path: "/api/admin/tasks/:taskId/unassign",
    pathParams: z10.object({ taskId: z10.string() }),
    body: z10.object({}),
    responses: {
      200: DeliveryTaskSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Unassign a task from its current driver"
  },
  cancel: {
    method: "POST",
    path: "/api/admin/tasks/:taskId/cancel",
    pathParams: z10.object({ taskId: z10.string() }),
    body: z10.object({ reason: z10.string().optional() }),
    responses: {
      200: DeliveryTaskSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Cancel a delivery task"
  },
  delete: {
    method: "DELETE",
    path: "/api/admin/tasks/:taskId",
    pathParams: z10.object({ taskId: z10.string() }),
    body: z10.object({}),
    responses: {
      204: z10.undefined(),
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Delete a delivery task (only pending or failed tasks can be deleted)"
  },
  getTimeline: {
    method: "GET",
    path: "/api/admin/tasks/:taskId/timeline",
    pathParams: z10.object({ taskId: z10.string() }),
    responses: {
      200: z10.array(TimelineEntrySchema),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get task timeline/event history"
  },
  sync: {
    method: "POST",
    path: "/api/admin/tasks/sync",
    body: z10.object({}),
    responses: {
      200: SyncResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Sync all tasks from Onfleet to local DB"
  },
  createLinked: {
    method: "POST",
    path: "/api/admin/tasks/linked",
    body: CreateLinkedTasksRequestSchema,
    responses: {
      201: LinkedTasksResponseSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create linked pickup+dropoff task pair"
  },
  getLinkedTask: {
    method: "GET",
    path: "/api/admin/tasks/:taskId/linked",
    pathParams: z10.object({ taskId: z10.string() }),
    responses: {
      200: DeliveryTaskSchema.nullable(),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get the linked task (pickup/dropoff pair) for a task"
  }
});
var adminDriversContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/drivers",
    query: ListDriversQuerySchema,
    responses: {
      200: z10.array(DriverSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all drivers"
  },
  create: {
    method: "POST",
    path: "/api/admin/drivers",
    body: CreateDriverRequestSchema,
    responses: {
      201: DriverSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new driver"
  },
  getById: {
    method: "GET",
    path: "/api/admin/drivers/:driverId",
    pathParams: z10.object({ driverId: z10.string() }),
    responses: {
      200: DriverSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get driver by ID"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/drivers/:driverId",
    pathParams: z10.object({ driverId: z10.string() }),
    body: UpdateDriverRequestSchema,
    responses: {
      200: DriverSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update a driver"
  },
  getTasks: {
    method: "GET",
    path: "/api/admin/drivers/:driverId/tasks",
    pathParams: z10.object({ driverId: z10.string() }),
    query: z10.object({
      status: DeliveryTaskStatusSchema.optional(),
      limit: z10.coerce.number().default(50)
    }),
    responses: {
      200: z10.array(DeliveryTaskSchema),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get a driver's assigned tasks"
  },
  getTeams: {
    method: "GET",
    path: "/api/admin/drivers/:driverId/teams",
    pathParams: z10.object({ driverId: z10.string() }),
    responses: {
      200: z10.array(z10.string()),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get a driver's team IDs"
  }
});
var adminTeamsContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/teams",
    responses: {
      200: z10.array(TeamSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all teams"
  },
  getById: {
    method: "GET",
    path: "/api/admin/teams/:teamId",
    pathParams: z10.object({ teamId: z10.string() }),
    responses: {
      200: TeamSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get team by ID"
  },
  create: {
    method: "POST",
    path: "/api/admin/teams",
    body: CreateTeamRequestSchema,
    responses: {
      201: TeamSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new team"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/teams/:teamId",
    pathParams: z10.object({ teamId: z10.string() }),
    body: UpdateTeamRequestSchema,
    responses: {
      200: TeamSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update a team"
  },
  delete: {
    method: "DELETE",
    path: "/api/admin/teams/:teamId",
    pathParams: z10.object({ teamId: z10.string() }),
    body: z10.object({}),
    responses: {
      204: z10.undefined(),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Delete a team"
  },
  autoDispatch: {
    method: "POST",
    path: "/api/admin/teams/:teamId/auto-dispatch",
    pathParams: z10.object({ teamId: z10.string() }),
    body: TeamAutoDispatchRequestSchema,
    responses: {
      200: TeamAutoDispatchResponseSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Trigger auto-dispatch for a team"
  },
  sync: {
    method: "POST",
    path: "/api/admin/teams/sync",
    body: z10.object({}),
    responses: {
      200: SyncResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Sync all teams from Onfleet to local DB"
  },
  getMembers: {
    method: "GET",
    path: "/api/admin/teams/:teamId/members",
    pathParams: z10.object({ teamId: z10.string() }),
    responses: {
      200: TeamMembersSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get team members (workers and managers) from junction tables"
  }
});
var adminHubsContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/hubs",
    responses: {
      200: z10.array(HubSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all hubs"
  },
  getById: {
    method: "GET",
    path: "/api/admin/hubs/:hubId",
    pathParams: z10.object({ hubId: z10.string() }),
    responses: {
      200: HubSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get hub by ID"
  },
  create: {
    method: "POST",
    path: "/api/admin/hubs",
    body: CreateHubRequestSchema,
    responses: {
      201: HubSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new hub"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/hubs/:hubId",
    pathParams: z10.object({ hubId: z10.string() }),
    body: UpdateHubRequestSchema,
    responses: {
      200: HubSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update a hub"
  },
  sync: {
    method: "POST",
    path: "/api/admin/hubs/sync",
    body: z10.object({}),
    responses: {
      200: SyncResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Sync all hubs from Onfleet to local DB"
  }
});
var DestinationLocationSchema = z10.object({
  lat: z10.number(),
  lng: z10.number()
});
var DestinationAddressSchema = z10.object({
  name: z10.string().optional(),
  number: z10.string(),
  street: z10.string(),
  apartment: z10.string().optional(),
  city: z10.string(),
  state: z10.string().optional(),
  postalCode: z10.string().optional(),
  country: z10.string()
});
var DestinationSchema = z10.object({
  id: z10.string(),
  location: DestinationLocationSchema,
  address: DestinationAddressSchema,
  notes: z10.string().optional(),
  googlePlaceId: z10.string().optional(),
  createdAt: z10.string().datetime(),
  updatedAt: z10.string().datetime()
});
var CreateDestinationAddressSchema = z10.object({
  name: z10.string().optional(),
  number: z10.string().optional(),
  street: z10.string().optional(),
  apartment: z10.string().optional(),
  city: z10.string().optional(),
  state: z10.string().optional(),
  postalCode: z10.string().optional(),
  country: z10.string().optional(),
  unparsed: z10.string().optional()
});
var CreateDestinationRequestSchema = z10.object({
  address: CreateDestinationAddressSchema,
  location: DestinationLocationSchema.optional(),
  notes: z10.string().optional()
});
var RecipientSchema = z10.object({
  id: z10.string(),
  name: z10.string(),
  phone: z10.string(),
  notes: z10.string().optional(),
  skipSMSNotifications: z10.boolean(),
  createdAt: z10.string().datetime(),
  updatedAt: z10.string().datetime()
});
var CreateRecipientRequestSchema = z10.object({
  name: z10.string().min(1, "Name is required"),
  phone: z10.string().min(1, "Phone is required"),
  notes: z10.string().optional(),
  skipSMSNotifications: z10.boolean().optional(),
  skipPhoneNumberValidation: z10.boolean().optional()
});
var UpdateRecipientRequestSchema = z10.object({
  name: z10.string().optional(),
  notes: z10.string().optional(),
  skipSMSNotifications: z10.boolean().optional()
});
var RecipientSearchQuerySchema = z10.object({
  name: z10.string().optional(),
  phone: z10.string().optional(),
  skipPhoneNumberValidation: z10.coerce.boolean().optional()
});
var ListDestinationsQuerySchema = z10.object({
  city: z10.string().optional(),
  state: z10.string().optional(),
  googlePlaceId: z10.string().optional(),
  limit: z10.coerce.number().optional()
});
var adminDestinationsContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/destinations",
    query: ListDestinationsQuerySchema,
    responses: {
      200: z10.array(DestinationSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all destinations from local DB cache"
  },
  getById: {
    method: "GET",
    path: "/api/admin/destinations/:destinationId",
    pathParams: z10.object({ destinationId: z10.string() }),
    responses: {
      200: DestinationSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get destination by ID"
  },
  create: {
    method: "POST",
    path: "/api/admin/destinations",
    body: CreateDestinationRequestSchema,
    responses: {
      201: DestinationSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new destination"
  },
  sync: {
    method: "POST",
    path: "/api/admin/destinations/sync",
    body: z10.object({}),
    responses: {
      200: SyncResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get sync status for destinations (Onfleet has no list endpoint)"
  }
});
var ListRecipientsQuerySchema = z10.object({
  name: z10.string().optional(),
  phone: z10.string().optional(),
  limit: z10.coerce.number().optional()
});
var adminRecipientsContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/recipients",
    query: ListRecipientsQuerySchema,
    responses: {
      200: z10.array(RecipientSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all recipients from local DB cache"
  },
  getById: {
    method: "GET",
    path: "/api/admin/recipients/:recipientId",
    pathParams: z10.object({ recipientId: z10.string() }),
    responses: {
      200: RecipientSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get recipient by ID"
  },
  search: {
    method: "GET",
    path: "/api/admin/recipients/search",
    query: RecipientSearchQuerySchema,
    responses: {
      200: RecipientSchema.nullable(),
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Search for a recipient by name or phone"
  },
  create: {
    method: "POST",
    path: "/api/admin/recipients",
    body: CreateRecipientRequestSchema,
    responses: {
      201: RecipientSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new recipient"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/recipients/:recipientId",
    pathParams: z10.object({ recipientId: z10.string() }),
    body: UpdateRecipientRequestSchema,
    responses: {
      200: RecipientSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update a recipient"
  },
  sync: {
    method: "POST",
    path: "/api/admin/recipients/sync",
    body: z10.object({}),
    responses: {
      200: SyncResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get sync status for recipients (Onfleet has no list endpoint)"
  }
});
var VehicleSchema2 = z10.object({
  id: z10.string(),
  type: VehicleTypeSchema2,
  description: z10.string().optional(),
  make: z10.string().optional(),
  model: z10.string().optional(),
  licensePlate: z10.string().optional(),
  color: z10.string().optional(),
  status: VehicleStatusSchema2,
  teamId: z10.string().optional(),
  capacity: z10.number().optional(),
  notes: z10.string().optional(),
  createdAt: z10.string().datetime(),
  updatedAt: z10.string().datetime()
});
var CreateVehicleRequestSchema = z10.object({
  type: VehicleTypeSchema2,
  description: z10.string().optional(),
  make: z10.string().optional(),
  model: z10.string().optional(),
  licensePlate: z10.string().optional(),
  color: z10.string().optional(),
  teamId: z10.string().optional(),
  capacity: z10.number().optional(),
  notes: z10.string().optional()
});
var UpdateVehicleRequestSchema = z10.object({
  type: VehicleTypeSchema2.optional(),
  description: z10.string().nullable().optional(),
  make: z10.string().nullable().optional(),
  model: z10.string().nullable().optional(),
  licensePlate: z10.string().nullable().optional(),
  color: z10.string().nullable().optional(),
  status: VehicleStatusSchema2.optional(),
  teamId: z10.string().optional(),
  capacity: z10.number().nullable().optional(),
  notes: z10.string().nullable().optional()
});
var ListVehiclesQuerySchema = z10.object({
  status: VehicleStatusSchema2.optional(),
  teamId: z10.string().optional(),
  type: VehicleTypeSchema2.optional(),
  limit: z10.coerce.number().optional()
});
var AssignVehicleRequestSchema = z10.object({
  driverId: z10.string()
});
var adminVehiclesContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/vehicles",
    query: ListVehiclesQuerySchema,
    responses: {
      200: z10.array(VehicleSchema2),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all vehicles"
  },
  getById: {
    method: "GET",
    path: "/api/admin/vehicles/:vehicleId",
    pathParams: z10.object({ vehicleId: z10.string() }),
    responses: {
      200: VehicleSchema2,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get vehicle by ID"
  },
  getAvailable: {
    method: "GET",
    path: "/api/admin/vehicles/available",
    query: z10.object({ teamId: z10.string().optional() }),
    responses: {
      200: z10.array(VehicleSchema2),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get available vehicles (not assigned)"
  },
  create: {
    method: "POST",
    path: "/api/admin/vehicles",
    body: CreateVehicleRequestSchema,
    responses: {
      201: VehicleSchema2,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new vehicle"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/vehicles/:vehicleId",
    pathParams: z10.object({ vehicleId: z10.string() }),
    body: UpdateVehicleRequestSchema,
    responses: {
      200: VehicleSchema2,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update a vehicle"
  },
  delete: {
    method: "DELETE",
    path: "/api/admin/vehicles/:vehicleId",
    pathParams: z10.object({ vehicleId: z10.string() }),
    body: z10.object({}),
    responses: {
      204: z10.undefined(),
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Delete a vehicle"
  },
  assign: {
    method: "POST",
    path: "/api/admin/vehicles/:vehicleId/assign",
    pathParams: z10.object({ vehicleId: z10.string() }),
    body: AssignVehicleRequestSchema,
    responses: {
      200: VehicleSchema2,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Assign a vehicle to a driver"
  },
  unassign: {
    method: "POST",
    path: "/api/admin/vehicles/:vehicleId/unassign",
    pathParams: z10.object({ vehicleId: z10.string() }),
    body: z10.object({}),
    responses: {
      200: VehicleSchema2,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Unassign a vehicle from its driver"
  }
});
var RouteOptimizationModeSchema = z10.enum(["minimize_distance", "always_on_time"]);
var RouteOptimizationParamsSchema = z10.object({
  mode: RouteOptimizationModeSchema,
  serviceTimeMinutes: z10.number().min(1).max(30).default(5)
});
var InitializeOptimizationRequestSchema = z10.object({
  teamId: z10.string().optional(),
  date: z10.string().optional(),
  taskIds: z10.array(z10.string()).optional(),
  params: RouteOptimizationParamsSchema,
  timezone: z10.string().default("America/Los_Angeles")
}).refine(
  (data) => data.teamId && data.date || data.taskIds && data.taskIds.length > 0,
  { message: "Must provide either teamId+date or taskIds" }
);
var OptimizationIssueSchema = z10.object({
  type: z10.enum(["warning", "error"]),
  message: z10.string(),
  entityId: z10.string().optional(),
  reason: z10.string()
});
var InitializeOptimizationResponseSchema = z10.object({
  optimizationId: z10.string(),
  shortId: z10.string(),
  issues: z10.array(OptimizationIssueSchema),
  summary: z10.object({
    workersCount: z10.number(),
    tasksCount: z10.number()
  }).optional()
});
var OptimizationMetricsSchema = z10.object({
  totalTransitTime: z10.number(),
  totalTransitDistance: z10.number(),
  totalServiceTime: z10.number()
});
var OptimizationWorkerMetricsSchema = z10.object({
  travelDistance: z10.number(),
  travelTime: z10.number(),
  taskCount: z10.number()
});
var OptimizationResultSummarySchema = z10.object({
  assigned: z10.number(),
  unassigned: z10.number(),
  cost: z10.number(),
  metrics: OptimizationMetricsSchema,
  workers: z10.record(OptimizationWorkerMetricsSchema)
});
var OptimizationStatusResponseSchema = z10.discriminatedUnion("status", [
  z10.object({ status: z10.literal("pending"), progress: z10.number() }),
  z10.object({
    status: z10.literal("success"),
    optimizationId: z10.string(),
    summary: OptimizationResultSummarySchema
  }),
  z10.object({ status: z10.literal("failed"), optimizationId: z10.string().optional() })
]);
var AbortOptimizationResponseSchema = z10.object({
  success: z10.boolean()
});
var GetTeamTasksQuerySchema = z10.object({
  teamId: z10.string(),
  date: z10.string()
});
var adminRouteOptimizationContract = c9.router({
  initialize: {
    method: "POST",
    path: "/api/admin/route-optimization/initialize",
    body: InitializeOptimizationRequestSchema,
    responses: {
      200: InitializeOptimizationResponseSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Initialize route optimization (standard mode)"
  },
  start: {
    method: "POST",
    path: "/api/admin/route-optimization/:optimizationId/start",
    pathParams: z10.object({ optimizationId: z10.string() }),
    body: z10.object({}),
    responses: {
      200: z10.object({ started: z10.boolean() }),
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Start route optimization calculation"
  },
  getStatus: {
    method: "GET",
    path: "/api/admin/route-optimization/:optimizationId/status",
    pathParams: z10.object({ optimizationId: z10.string() }),
    responses: {
      200: OptimizationStatusResponseSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get route optimization status"
  },
  abort: {
    method: "POST",
    path: "/api/admin/route-optimization/:optimizationId/abort",
    pathParams: z10.object({ optimizationId: z10.string() }),
    body: z10.object({}),
    responses: {
      200: AbortOptimizationResponseSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Abort route optimization"
  },
  apply: {
    method: "POST",
    path: "/api/admin/route-optimization/:optimizationId/apply",
    pathParams: z10.object({ optimizationId: z10.string() }),
    body: z10.object({}),
    responses: {
      200: z10.object({ applied: z10.boolean() }),
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Apply route optimization results"
  },
  getTeamTasks: {
    method: "GET",
    path: "/api/admin/route-optimization/team-tasks",
    query: GetTeamTasksQuerySchema,
    responses: {
      200: z10.array(DeliveryTaskSchema),
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get unassigned tasks for a team on a specific date"
  }
});
var B2BWaveOrderProductSummarySchema = z10.object({
  id: z10.number(),
  productId: z10.number(),
  productName: z10.string().optional(),
  productCode: z10.string().optional(),
  quantity: z10.string(),
  price: z10.string(),
  totalAfterVat: z10.string()
});
var B2BWaveOrderSummarySchema = z10.object({
  id: z10.number(),
  statusOrderId: z10.number(),
  statusOrderName: z10.string().optional(),
  customerId: z10.number(),
  customerCompany: z10.string().optional(),
  customerName: z10.string().optional(),
  customerPhone: z10.string().optional(),
  customerEmail: z10.string().optional(),
  grossTotal: z10.string(),
  currency: z10.string().optional(),
  submittedAt: z10.string().nullable().optional(),
  address: z10.string().nullable(),
  city: z10.string().nullable().optional(),
  province: z10.string().nullable().optional(),
  postalCode: z10.string().nullable().optional(),
  country: z10.string().nullable(),
  commentsCustomer: z10.string().nullable().optional(),
  commentsWholesaler: z10.string().nullable().optional(),
  orderProducts: z10.array(B2BWaveOrderProductSummarySchema)
});
var ListOrdersQuerySchema = z10.object({
  statusIds: z10.array(z10.coerce.number()).optional(),
  limit: z10.coerce.number().default(50),
  customerId: z10.coerce.number().optional()
});
var adminOrdersContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/orders",
    query: ListOrdersQuerySchema,
    responses: {
      200: z10.array(B2BWaveOrderSummarySchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List B2BWave orders for delivery task creation"
  },
  getById: {
    method: "GET",
    path: "/api/admin/orders/:orderId",
    pathParams: z10.object({ orderId: z10.coerce.number() }),
    responses: {
      200: B2BWaveOrderSummarySchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get B2BWave order by ID"
  }
});
var webhooksContract = c9.router({
  onfleet: {
    method: "POST",
    path: "/api/webhooks/onfleet",
    body: z10.any(),
    // Onfleet sends various payloads
    responses: {
      200: z10.object({ received: z10.boolean() }),
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Receive Onfleet webhook events"
  },
  // Onfleet URL validation (GET request with check query param)
  onfleetValidate: {
    method: "GET",
    path: "/api/webhooks/onfleet",
    query: z10.object({ check: z10.string() }),
    responses: {
      200: z10.string(),
      // Returns the check value
      400: errorResponse9
    },
    summary: "Onfleet webhook URL validation"
  },
  // EasyPost tracker.updated webhook
  easypost: {
    method: "POST",
    path: "/api/webhooks/easypost",
    body: z10.any(),
    // EasyPost sends various event payloads
    responses: {
      200: z10.object({ received: z10.boolean() }),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Receive EasyPost tracker webhook events"
  }
});
var OnfleetWebhookTriggerSchema = z10.number().int().min(0).max(25);
var OnfleetWebhookSchema = z10.object({
  id: z10.string(),
  url: z10.string().url(),
  trigger: OnfleetWebhookTriggerSchema,
  threshold: z10.number().optional(),
  isEnabled: z10.boolean().optional(),
  name: z10.string().optional()
});
var CreateOnfleetWebhookRequestSchema = z10.object({
  url: z10.string().url("Valid URL is required"),
  trigger: OnfleetWebhookTriggerSchema,
  threshold: z10.number().optional(),
  name: z10.string().optional()
});
var adminOnfleetWebhooksContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/onfleet-webhooks",
    responses: {
      200: z10.array(OnfleetWebhookSchema),
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List all Onfleet webhooks"
  },
  create: {
    method: "POST",
    path: "/api/admin/onfleet-webhooks",
    body: CreateOnfleetWebhookRequestSchema,
    responses: {
      201: OnfleetWebhookSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Create a new Onfleet webhook"
  },
  delete: {
    method: "DELETE",
    path: "/api/admin/onfleet-webhooks/:webhookId",
    pathParams: z10.object({ webhookId: z10.string() }),
    body: z10.object({}),
    responses: {
      204: z10.undefined(),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Delete an Onfleet webhook"
  }
});
var DriverReconcileMetricsSchema = z10.object({
  driversChecked: z10.number(),
  created: z10.number(),
  updated: z10.number(),
  unchanged: z10.number(),
  errors: z10.number()
});
var ReconcileDriversResponseSchema = z10.object({
  success: z10.boolean(),
  metrics: DriverReconcileMetricsSchema
});
var PollDriverLocationRequestSchema = z10.object({
  taskId: z10.string().uuid(),
  onfleetTaskId: z10.string().min(1),
  driverId: z10.string().uuid(),
  onfleetWorkerId: z10.string().min(1),
  startedAt: z10.string().datetime()
});
var PollDriverLocationResponseSchema = z10.object({
  status: z10.enum(["success", "task_terminal", "no_location", "error"]),
  location: z10.object({ lat: z10.number(), lng: z10.number() }).optional(),
  capturedAt: z10.string().datetime().optional(),
  isStale: z10.boolean().optional(),
  removed: z10.boolean().optional(),
  message: z10.string().optional()
});
var ReconcileFulfillmentsRequestSchema = z10.object({
  staleHoursThreshold: z10.number().int().positive().optional().default(6),
  alertDaysThreshold: z10.number().int().positive().optional().default(7)
});
var ReconcileFulfillmentsMetricsSchema = z10.object({
  stalePartitionsChecked: z10.number(),
  partitionsHealed: z10.number(),
  orphanedReservationsReleased: z10.number(),
  staleReservationAlerts: z10.number(),
  legConsistencyChecks: z10.number(),
  legInconsistenciesDetected: z10.number(),
  nonTerminalLegCompletionsSkipped: z10.number(),
  errors: z10.number()
});
var LegConsistencyIssueSchema = z10.object({
  partitionId: z10.string().uuid(),
  onfleetTaskId: z10.string(),
  shipmentLegId: z10.string().uuid().nullable(),
  issue: z10.enum([
    "missing_leg_for_completed_task",
    "missing_leg_allocation",
    "leg_not_completed_for_completed_task",
    "terminal_allocation_not_completed",
    "non_terminal_allocation_not_transferred"
  ]),
  expectedState: z10.string().optional(),
  actualState: z10.string().optional()
});
var ReconcileFulfillmentsResponseSchema = z10.object({
  success: z10.boolean(),
  metrics: ReconcileFulfillmentsMetricsSchema,
  legConsistencyIssues: z10.array(LegConsistencyIssueSchema)
});
var ReconcileShipmentLegMetrcRefsRequestSchema = z10.object({
  organizationId: z10.string().uuid(),
  fromDate: z10.string().datetime().optional(),
  toDate: z10.string().datetime().optional(),
  limit: z10.number().int().positive().optional().default(1e3)
});
var ReconcileShipmentLegMetrcRefsScopeSchema = z10.object({
  organizationId: z10.string().uuid(),
  fromDate: z10.string().datetime().optional(),
  toDate: z10.string().datetime().optional(),
  limit: z10.number().int().positive()
});
var ReconcileShipmentLegMetrcRefsResponseSchema = z10.object({
  success: z10.boolean(),
  report: z10.object({
    scope: ReconcileShipmentLegMetrcRefsScopeSchema,
    metrics: ShipmentLegMetrcReconciliationMetricsSchema,
    issues: z10.array(ShipmentLegMetrcReconciliationIssueSchema)
  })
});
var CheckCarrierTrackingMetricsSchema = z10.object({
  checked: z10.number(),
  updated: z10.number(),
  completed: z10.number(),
  errors: z10.number()
});
var CheckCarrierTrackingResponseSchema = z10.object({
  success: z10.boolean(),
  metrics: CheckCarrierTrackingMetricsSchema
});
var SyncDeliveryProofAssetRequestSchema = z10.object({
  proofAssetId: z10.string().uuid()
});
var SyncDeliveryProofAssetResponseSchema = z10.object({
  success: z10.boolean(),
  proofAssetId: z10.string().uuid(),
  status: z10.enum(["synced", "skipped"]),
  skippedReason: z10.enum(["not_found", "already_synced"]).optional(),
  partitionId: z10.string().uuid().optional(),
  taskId: z10.string().uuid().optional(),
  assetType: DeliveryProofAssetTypeSchema.optional(),
  onfleetUploadId: z10.string().optional(),
  sourceUrl: z10.string().optional(),
  publicUrl: z10.string().optional(),
  r2Bucket: z10.string().optional(),
  r2Key: z10.string().optional()
});
var jobsContract = c9.router({
  reconcileDrivers: {
    method: "POST",
    path: "/api/jobs/delivery/reconcile-drivers",
    body: z10.object({}),
    responses: {
      200: ReconcileDriversResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Reconcile all drivers from Onfleet (job worker endpoint)"
  },
  pollDriverLocation: {
    method: "POST",
    path: "/api/jobs/delivery/poll-driver-location",
    body: PollDriverLocationRequestSchema,
    responses: {
      200: PollDriverLocationResponseSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Poll driver location from Onfleet and store in history (job worker endpoint)"
  },
  reconcileFulfillments: {
    method: "POST",
    path: "/api/jobs/delivery/reconcile-fulfillments",
    body: ReconcileFulfillmentsRequestSchema,
    responses: {
      200: ReconcileFulfillmentsResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Reconcile stale partitions and orphaned inventory reservations (job worker endpoint)"
  },
  checkCarrierTracking: {
    method: "POST",
    path: "/api/jobs/delivery/check-carrier-tracking",
    body: z10.object({}),
    responses: {
      200: CheckCarrierTrackingResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Poll EasyPost for carrier tracking updates on stale shipments (job worker endpoint)"
  },
  syncDeliveryProofAsset: {
    method: "POST",
    path: "/api/jobs/delivery/sync-proof-asset",
    body: SyncDeliveryProofAssetRequestSchema,
    responses: {
      200: SyncDeliveryProofAssetResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Sync a persisted delivery proof asset to storage (job worker endpoint)"
  },
  reconcileShipmentLegMetrcRefs: {
    method: "POST",
    path: "/api/jobs/delivery/reconcile-shipment-leg-metrc-refs",
    body: ReconcileShipmentLegMetrcRefsRequestSchema,
    responses: {
      200: ReconcileShipmentLegMetrcRefsResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Build shipment-leg METRC reconciliation report (job worker endpoint)"
  }
});
var RoutePointSchema2 = z10.object({
  lat: z10.number(),
  lng: z10.number(),
  capturedAt: z10.string().datetime()
});
var GeoLocationSchema2 = z10.object({
  lat: z10.number(),
  lng: z10.number()
});
var TaskRouteSchema2 = z10.object({
  taskId: z10.string(),
  trackingId: z10.string(),
  driverId: z10.string(),
  driverName: z10.string(),
  status: DeliveryTaskStatusSchema,
  startPoint: GeoLocationSchema2.optional(),
  endPoint: GeoLocationSchema2,
  routePoints: z10.array(RoutePointSchema2),
  totalPoints: z10.number(),
  completedAt: z10.string().datetime().optional(),
  recipientName: z10.string(),
  recipientAddress: z10.string()
});
var RouteBoundsSchema2 = z10.object({
  sw: GeoLocationSchema2,
  ne: GeoLocationSchema2
});
var BatchRoutesResponseSchema2 = z10.object({
  routes: z10.array(TaskRouteSchema2),
  bounds: RouteBoundsSchema2.optional()
});
var GetRoutesQuerySchema = z10.object({
  taskIds: z10.array(z10.string()).optional(),
  driverId: z10.string().optional(),
  teamId: z10.string().optional(),
  status: DeliveryTaskStatusSchema.optional(),
  fromDate: z10.string().optional(),
  toDate: z10.string().optional(),
  simplify: z10.coerce.boolean().default(true),
  simplifyTolerance: z10.coerce.number().default(1e-4)
});
var adminRoutesContract = c9.router({
  getTaskRoute: {
    method: "GET",
    path: "/api/admin/routes/:taskId",
    pathParams: z10.object({ taskId: z10.string() }),
    query: z10.object({ simplify: z10.coerce.boolean().default(true) }),
    responses: {
      200: TaskRouteSchema2,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get route data for a single completed task"
  },
  getBatchRoutes: {
    method: "GET",
    path: "/api/admin/routes",
    query: GetRoutesQuerySchema,
    responses: {
      200: BatchRoutesResponseSchema2,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get route data for multiple tasks with filters"
  }
});
var CreatePartitionTaskRequestSchema = z10.object({
  partitionId: z10.string().uuid()
});
var PartitionTaskResultSchema = z10.object({
  task: DeliveryTaskSchema,
  partition: z10.object({
    id: z10.string().uuid(),
    trackingId: z10.string(),
    onfleetTaskId: z10.string()
  })
});
var PartitionTaskSkippedSchema = z10.object({
  skipped: z10.literal(true),
  reason: z10.string(),
  partition: z10.object({
    id: z10.string().uuid(),
    trackingId: z10.string()
  })
});
var SyncTeamResultSchema = z10.object({
  partitionId: z10.string().uuid(),
  onfleetTaskId: z10.string().nullable(),
  teamId: z10.string().nullable(),
  teamSource: z10.enum(["brand", "location", "none"]),
  updated: z10.boolean(),
  message: z10.string().optional()
});
var internalPartitionTasksContract = c9.router({
  createFromPartition: {
    method: "POST",
    path: "/api/internal/partition-tasks",
    body: CreatePartitionTaskRequestSchema,
    responses: {
      200: PartitionTaskSkippedSchema,
      // Mail carrier partitions skip Onfleet
      201: PartitionTaskResultSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      409: errorResponse9,
      // Partition already has a task
      500: errorResponse9
    },
    summary: "Create Onfleet delivery task from order partition (event-worker endpoint)"
  },
  hasTask: {
    method: "GET",
    path: "/api/internal/partition-tasks/:partitionId/has-task",
    pathParams: z10.object({ partitionId: z10.string().uuid() }),
    responses: {
      200: z10.object({ hasTask: z10.boolean() }),
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Check if partition already has a delivery task"
  },
  syncTeam: {
    method: "POST",
    path: "/api/internal/partition-tasks/:partitionId/sync-team",
    pathParams: z10.object({ partitionId: z10.string().uuid() }),
    body: z10.object({}),
    responses: {
      200: SyncTeamResultSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Sync Onfleet task team assignment based on partition fulfillment type (event-worker endpoint)"
  }
});
var InternalDatabaseStatusSchema = z10.object({
  failoverEnabled: z10.boolean(),
  secondaryConfigured: z10.boolean(),
  activeEndpoint: z10.enum(["primary", "secondary"]),
  poolGeneration: z10.number().int().nonnegative(),
  lastSwitchAt: z10.string().datetime().nullable(),
  lastSwitchReason: z10.string().nullable(),
  lastPoolError: z10.object({
    message: z10.string(),
    at: z10.string().datetime()
  }).nullable(),
  healthCheckTimeoutMs: z10.number().int().positive()
});
var InternalDatabaseHealthSchema = z10.object({
  healthy: z10.boolean(),
  endpoint: z10.enum(["primary", "secondary"]),
  generation: z10.number().int().nonnegative(),
  checkedAt: z10.string().datetime(),
  latencyMs: z10.number().int().nonnegative(),
  error: z10.string().optional()
});
var InternalDatabaseSwitchRequestSchema = z10.object({
  endpoint: z10.enum(["primary", "secondary"]),
  reason: z10.string().min(1).max(128).optional()
});
var InternalDatabaseSwitchResultSchema = z10.object({
  switched: z10.boolean(),
  previousEndpoint: z10.enum(["primary", "secondary"]),
  activeEndpoint: z10.enum(["primary", "secondary"]),
  poolGeneration: z10.number().int().nonnegative()
});
var InternalDatabaseFailoverStatusResponseSchema = z10.object({
  status: InternalDatabaseStatusSchema,
  health: InternalDatabaseHealthSchema
});
var InternalDatabaseFailoverSwitchResponseSchema = z10.object({
  result: InternalDatabaseSwitchResultSchema,
  status: InternalDatabaseStatusSchema,
  health: InternalDatabaseHealthSchema
});
var internalDatabaseFailoverContract = c9.router({
  status: {
    method: "GET",
    path: "/api/internal/database/failover/status",
    responses: {
      200: InternalDatabaseFailoverStatusResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Get current database failover status and health (internal)"
  },
  switchEndpoint: {
    method: "POST",
    path: "/api/internal/database/failover/switch",
    body: InternalDatabaseSwitchRequestSchema,
    responses: {
      200: InternalDatabaseFailoverSwitchResponseSchema,
      400: errorResponse9,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "Force active database endpoint switch (internal operator control)"
  }
});
var AdminPartitionFulfillmentTypeSchema = z10.enum(["brand_driver", "flote_driver", "mail_carrier"]);
var AdminPartitionFulfillmentStatusSchema = z10.enum([
  "pending_offer",
  "offer_sent",
  "accepted",
  "declined",
  "expired",
  "in_transit",
  "shipped",
  "assigned",
  "in_progress",
  "completed",
  "cancelled"
]);
var AdminPartitionItemSchema = z10.object({
  id: z10.string().uuid(),
  productVariantId: z10.string().uuid(),
  productName: z10.string().optional(),
  productCode: z10.string().optional(),
  quantity: z10.number().int().nonnegative(),
  reservedQuantity: z10.number().int().nonnegative(),
  fulfilledQuantity: z10.number().int().nonnegative(),
  unitPrice: z10.number(),
  lineTotal: z10.number()
});
var AdminOrderPartitionSchema = z10.object({
  id: z10.string().uuid(),
  orderId: z10.string().uuid().nullable(),
  b2bwaveOrderId: z10.string(),
  brandId: z10.string().uuid(),
  brandName: z10.string().optional(),
  trackingId: z10.string(),
  portionSubtotal: z10.number(),
  portionTotal: z10.number(),
  fulfillmentType: AdminPartitionFulfillmentTypeSchema.nullable(),
  fulfillmentStatus: AdminPartitionFulfillmentStatusSchema,
  paymentMethod: z10.string().nullable(),
  stockLocationId: z10.string().uuid().nullable(),
  stockLocationName: z10.string().optional(),
  onfleetTaskId: z10.string().nullable(),
  shippingCarrier: z10.string().nullable(),
  carrierTrackingNumber: z10.string().nullable(),
  shippedAt: z10.string().datetime().nullable(),
  spreadRate: z10.number().nullable(),
  spreadAmount: z10.number().nullable(),
  hasSignature: z10.boolean(),
  photoCount: z10.number().int().nonnegative(),
  metadata: z10.record(z10.unknown()),
  createdAt: z10.string().datetime(),
  updatedAt: z10.string().datetime()
});
var AdminOrderPartitionDetailSchema = AdminOrderPartitionSchema.extend({
  items: z10.array(AdminPartitionItemSchema),
  customerName: z10.string().optional(),
  customerPhone: z10.string().optional(),
  customerAddress: z10.string().optional()
});
var ListPartitionsQuerySchema = z10.object({
  fulfillmentType: AdminPartitionFulfillmentTypeSchema.optional(),
  fulfillmentStatus: AdminPartitionFulfillmentStatusSchema.optional(),
  brandId: z10.string().uuid().optional(),
  b2bwaveOrderId: z10.string().optional(),
  paymentMethod: z10.string().optional(),
  fromDate: z10.string().optional(),
  toDate: z10.string().optional(),
  limit: z10.coerce.number().default(50),
  cursor: z10.string().optional(),
  // Special filter for flote-managed (flote_driver OR null)
  floteManaged: z10.coerce.boolean().optional()
});
var UpdatePartitionRequestSchema = z10.object({
  fulfillmentType: AdminPartitionFulfillmentTypeSchema.optional(),
  fulfillmentStatus: AdminPartitionFulfillmentStatusSchema.optional(),
  stockLocationId: z10.string().uuid().nullable().optional()
});
var PaginatedPartitionsResponseSchema = z10.object({
  items: z10.array(AdminOrderPartitionSchema),
  nextCursor: z10.string().optional(),
  totalCount: z10.number().optional()
});
var adminPartitionsContract = c9.router({
  list: {
    method: "GET",
    path: "/api/admin/partitions",
    query: ListPartitionsQuerySchema,
    responses: {
      200: PaginatedPartitionsResponseSchema,
      401: errorResponse9,
      500: errorResponse9
    },
    summary: "List order partitions with filters"
  },
  getById: {
    method: "GET",
    path: "/api/admin/partitions/:partitionId",
    pathParams: z10.object({ partitionId: z10.string().uuid() }),
    responses: {
      200: AdminOrderPartitionDetailSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get partition by ID with items and customer info"
  },
  getByTrackingId: {
    method: "GET",
    path: "/api/admin/partitions/tracking/:trackingId",
    pathParams: z10.object({ trackingId: z10.string() }),
    responses: {
      200: AdminOrderPartitionDetailSchema,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Get partition by tracking ID"
  },
  update: {
    method: "PATCH",
    path: "/api/admin/partitions/:partitionId",
    pathParams: z10.object({ partitionId: z10.string().uuid() }),
    body: UpdatePartitionRequestSchema,
    responses: {
      200: AdminOrderPartitionSchema,
      400: errorResponse9,
      401: errorResponse9,
      404: errorResponse9,
      500: errorResponse9
    },
    summary: "Update partition (admin override for fulfillment type, status, stock location)"
  }
});
var deliveryGatewayContract = c9.router({
  tracking: trackingContract,
  adminAuth: adminAuthContract,
  adminTasks: adminTasksContract,
  adminDrivers: adminDriversContract,
  adminTeams: adminTeamsContract,
  adminHubs: adminHubsContract,
  adminDestinations: adminDestinationsContract,
  adminRecipients: adminRecipientsContract,
  adminVehicles: adminVehiclesContract,
  adminDashboard: adminDashboardContract,
  adminOnfleetWebhooks: adminOnfleetWebhooksContract,
  adminRouteOptimization: adminRouteOptimizationContract,
  adminRoutes: adminRoutesContract,
  adminOrders: adminOrdersContract,
  adminPartitions: adminPartitionsContract,
  webhooks: webhooksContract,
  jobs: jobsContract,
  internalPartitionTasks: internalPartitionTasksContract,
  internalAssignmentQueue: internalAssignmentQueueContract,
  internalDatabaseFailover: internalDatabaseFailoverContract,
  supplierAssignmentQueue: assignmentQueueContract,
  supplierShipmentLegs: shipmentLegsContract,
  supplierRoutePlans: supplierRoutePlansContract,
  auxiliaryTasks: auxiliaryTasksContract
});

export {
  ListAssignmentQueueQuerySchema,
  GetAssignmentQueueStatsQuerySchema,
  EnrichedQueueEntrySchema,
  QueueListResponseSchema,
  UpdatePriorityRequestSchema,
  assignmentQueueContract,
  EnqueuePartitionRequestSchema,
  EnqueuePartitionResponseSchema,
  AutoEnqueueRequestSchema,
  AutoEnqueueResponseSchema,
  internalAssignmentQueueContract,
  DeliveryTaskStatusSchema,
  DeliveryTaskTypeSchema,
  DriverStatusSchema,
  DeliveryTaskSchema,
  DriverSchema,
  TeamSchema,
  CreateSupplierDriverInputSchema,
  UpdateSupplierDriverInputSchema,
  UpdateDriverTeamsInputSchema,
  ListSupplierDriversQuerySchema,
  DriverTasksQuerySchema,
  TaskAssignmentResultSchema,
  AssignDriverToPartitionRequestSchema,
  PhotoUploadResponseSchema,
  DriverAnalyticsQuerySchema,
  DriverAnalyticsSchema,
  DriverPoolQuerySchema,
  DriverPoolResponseSchema,
  supplierDriversContract,
  SupplierLocationTeamSchema,
  SupplierLocationTeamWithDetailsSchema,
  CreateSupplierLocationTeamRequestSchema,
  SupplierDeliveryTeamSchema,
  UpdateSupplierDeliveryTeamRequestSchema,
  DeliveryTeamHubOptionSchema,
  DeliveryTeamManagerSchema,
  AddDeliveryTeamManagerRequestSchema,
  supplierDeliveryTeamContract,
  VEHICLE_TYPES_REQUIRING_MAKE_MODEL,
  vehicleTypeRequiresMakeAndModel,
  CreateSupplierVehicleInputSchema,
  UpdateSupplierVehicleInputSchema,
  ListSupplierVehiclesQuerySchema,
  AssignVehicleToDriverRequestSchema,
  supplierVehiclesContract,
  GetSupplierRoutesQuerySchema,
  supplierRoutesContract,
  CreateShipmentFromPartitionsConflictSchema,
  ListShipmentLegQueueQuerySchema,
  ShipmentLegQueueListResponseSchema,
  ListShipmentSummariesQuerySchema,
  ShipmentQueueSummariesResponseSchema,
  AssignDriverToShipmentLegRequestSchema,
  AssignDriverToShipmentLegResponseSchema,
  CreateShipmentPlanRequestSchema,
  CreateShipmentPlanQueueErrorSchema,
  CreateShipmentPlanResponseSchema,
  DispatchShipmentRequestSchema,
  AddHubSplitRequestSchema,
  AddHubSplitResponseSchema,
  BatchInsertHubRequestSchema,
  BatchInsertHubResponseSchema,
  UpdateHubMetadataRequestSchema,
  UpdateHubMetadataResponseSchema,
  RemoveHubSplitRequestSchema,
  RemoveHubSplitResponseSchema,
  InsertWaypointRequestSchema,
  InsertWaypointResponseSchema,
  ReorderStopsRequestSchema,
  ReorderStopsResponseSchema,
  OptimizeShipmentRouteRequestSchema,
  OptimizeShipmentRouteResponseSegmentSchema,
  OptimizeShipmentRouteResponseSchema,
  CreateShipmentFromPartitionsRequestSchema,
  CreateShipmentFromPartitionsResponseSchema,
  AssignDestinationLicenseRequestSchema,
  AssignDestinationLicenseResponseSchema,
  DriverPositionSchema,
  GetShipmentDriverPositionsResponseSchema,
  ShipmentDetailResponseSchema,
  DeleteShipmentRequestSchema,
  DeleteShipmentResponseSchema,
  DeleteShipmentLegRequestSchema,
  DeleteShipmentLegResponseSchema,
  CancelQueueEntryResponseSchema,
  UpdateShipmentLegRequestSchema,
  UpdateShipmentLegResponseSchema,
  GetShipmentLegManifestResponseSchema,
  PrepareShipmentLegManifestRequestSchema,
  PrepareShipmentLegManifestResponseSchema,
  SubmitShipmentLegManifestRequestSchema,
  UpdateShipmentLegManifestRequestSchema,
  VoidShipmentLegManifestRequestSchema,
  ShipmentLegManifestActionResponseSchema,
  PrepareShipmentManifestsRequestSchema,
  PrepareShipmentManifestsResponseSchema,
  SubmitShipmentManifestRequestSchema,
  ShipmentManifestActionResponseSchema,
  shipmentLegsContract,
  RoutePlanSchema,
  RoutePlanSummarySchema,
  CreateRoutePlanInputSchema,
  ListRoutePlansQuerySchema,
  UpdateRoutePlanTasksInputSchema,
  supplierRoutePlansContract,
  CreateAuxiliaryTaskBodySchema,
  CreateAuxiliaryTaskResponseSchema,
  auxiliaryTasksContract,
  DashboardStatsSchema,
  AuthValidationResponseSchema,
  adminDashboardContract,
  SyncResponseSchema,
  PublicTrackingResponseSchema,
  ListTasksQuerySchema,
  ListDriversQuerySchema,
  VehicleTypeSchema2 as VehicleTypeSchema,
  VehicleStatusSchema2 as VehicleStatusSchema,
  CreateDriverVehicleInputSchema,
  CreateDriverRequestSchema,
  UpdateDriverRequestSchema,
  CompletionRequirementsSchema,
  TaskAddressSchema,
  CreateTaskRequestSchema,
  CreateLinkedTasksRequestSchema,
  LinkedTasksResponseSchema,
  UpdateTaskRequestSchema,
  AssignTaskRequestSchema,
  TimelineEntrySchema,
  CreateTeamRequestSchema,
  UpdateTeamRequestSchema,
  TeamMembersSchema,
  TeamAutoDispatchRequestSchema,
  TeamAutoDispatchResponseSchema,
  HubAddressSchema,
  HubLocationSchema,
  HubSchema,
  CreateHubAddressSchema,
  CreateHubRequestSchema,
  UpdateHubRequestSchema,
  trackingContract,
  adminAuthContract,
  adminTasksContract,
  adminDriversContract,
  adminTeamsContract,
  adminHubsContract,
  DestinationLocationSchema,
  DestinationAddressSchema,
  DestinationSchema,
  CreateDestinationAddressSchema,
  CreateDestinationRequestSchema,
  RecipientSchema,
  CreateRecipientRequestSchema,
  UpdateRecipientRequestSchema,
  RecipientSearchQuerySchema,
  ListDestinationsQuerySchema,
  adminDestinationsContract,
  ListRecipientsQuerySchema,
  adminRecipientsContract,
  VehicleSchema2 as VehicleSchema,
  CreateVehicleRequestSchema,
  UpdateVehicleRequestSchema,
  ListVehiclesQuerySchema,
  AssignVehicleRequestSchema,
  adminVehiclesContract,
  RouteOptimizationModeSchema,
  RouteOptimizationParamsSchema,
  InitializeOptimizationRequestSchema,
  OptimizationIssueSchema,
  InitializeOptimizationResponseSchema,
  OptimizationMetricsSchema,
  OptimizationWorkerMetricsSchema,
  OptimizationResultSummarySchema,
  OptimizationStatusResponseSchema,
  AbortOptimizationResponseSchema,
  GetTeamTasksQuerySchema,
  adminRouteOptimizationContract,
  B2BWaveOrderProductSummarySchema,
  B2BWaveOrderSummarySchema,
  ListOrdersQuerySchema,
  adminOrdersContract,
  webhooksContract,
  OnfleetWebhookTriggerSchema,
  OnfleetWebhookSchema,
  CreateOnfleetWebhookRequestSchema,
  adminOnfleetWebhooksContract,
  DriverReconcileMetricsSchema,
  ReconcileDriversResponseSchema,
  PollDriverLocationRequestSchema,
  PollDriverLocationResponseSchema,
  ReconcileFulfillmentsRequestSchema,
  ReconcileFulfillmentsMetricsSchema,
  LegConsistencyIssueSchema,
  ReconcileFulfillmentsResponseSchema,
  ReconcileShipmentLegMetrcRefsRequestSchema,
  ReconcileShipmentLegMetrcRefsScopeSchema,
  ReconcileShipmentLegMetrcRefsResponseSchema,
  CheckCarrierTrackingMetricsSchema,
  CheckCarrierTrackingResponseSchema,
  SyncDeliveryProofAssetRequestSchema,
  SyncDeliveryProofAssetResponseSchema,
  jobsContract,
  RoutePointSchema2 as RoutePointSchema,
  GeoLocationSchema2 as GeoLocationSchema,
  TaskRouteSchema2 as TaskRouteSchema,
  RouteBoundsSchema2 as RouteBoundsSchema,
  BatchRoutesResponseSchema2 as BatchRoutesResponseSchema,
  GetRoutesQuerySchema,
  adminRoutesContract,
  CreatePartitionTaskRequestSchema,
  PartitionTaskResultSchema,
  PartitionTaskSkippedSchema,
  SyncTeamResultSchema,
  internalPartitionTasksContract,
  InternalDatabaseStatusSchema,
  InternalDatabaseHealthSchema,
  InternalDatabaseSwitchRequestSchema,
  InternalDatabaseSwitchResultSchema,
  InternalDatabaseFailoverStatusResponseSchema,
  InternalDatabaseFailoverSwitchResponseSchema,
  internalDatabaseFailoverContract,
  AdminPartitionFulfillmentTypeSchema,
  AdminPartitionFulfillmentStatusSchema,
  AdminPartitionItemSchema,
  AdminOrderPartitionSchema,
  AdminOrderPartitionDetailSchema,
  ListPartitionsQuerySchema,
  UpdatePartitionRequestSchema,
  PaginatedPartitionsResponseSchema,
  adminPartitionsContract,
  deliveryGatewayContract
};
